import { cloneDeep, difference, differenceWith, isEqual, sortBy, startCase } from "lodash";
import * as React from "react";
import { RouteComponentProps, withRouter } from "react-router";

import { CodeMapSet, CodeMapSetService, CodeMapType } from "@app/codemaps";
import { ClientInsuranceModel, InsuranceService } from "@app/insurance";
import { MemberSetModel, MemberSetService } from "@app/members";
import { Project, ProjectService } from "@app/projects";
import { SpecialtyOverrides } from "@app/projects/create/interfaces";
import EditProjectOverrideMemberSet from "@app/projects/editProjectOverrideMemberSet";
import { ProjectOverrideMemberSetType, TimeDistanceCalculatorType } from "@app/projects/enumerators";
import { Details } from "@app/projects/network-assessment/EditDetails";
import { EditDisplayOptions } from "@app/projects/network-assessment/EditDisplayOptions";
import { MemberOptions } from "@app/projects/network-assessment/EditMemberOptions";
import ProviderOptions from "@app/projects/network-assessment/EditProvidersOptions";
import { TemplateOptions } from "@app/projects/network-assessment/EditTemplateOptions";
import { CreateSpecialtyGroupException, SpecialtyGroupExceptionDisplay, SpecialtyGroupExceptionStatus } from "@app/projects/results/common";
import { ProviderSet, ProviderSetService } from "@app/provider-sets";
import { ResourceService, ResourceSharingUserModel, ResourceType } from "@app/resources";
import { AccordionContainer, Accordion, Breadcrumbs, LoadingSpinner, Page, PageHeader, PageHeaderLeft, PageHeaderRight, PageInner, PageSection } from "@app/shared";
import { QESAlert, QESButton, QESCheckbox, QESSelect, QESTextArea, QESTextInput } from "@app/shared/qes-standards";
import { AccessCriteriaGranularityEnum, AccuracySpecialtyDefinition, AccuracyTemplateCollectionMetadata, AccuracyTemplateService, TemplateCollectionMetadata, TemplateService, TemplateSpecialtyGroup } from "@app/templates";
import { RoleRestricted, Roles, UserContext, UserContextProps, withUserContext } from "@app/user";
import { CloneProjectModel, CopyProjectionProvider } from ".";
import { EditPerspective, ProjectExclusionType, ProjectionProviderType, ProjectRunConfigurationType, ProviderNetworkType } from "../enumerators";
import { MemberSetTypeEnum } from "@app/members/enumerations";
import Features from "@app/feature-flags/features";
import SpecialtyGroupExceptionsService from "../specialtyGroupExceptionsService";
import NetworkTaxonomyContentPanel from "@app/network-taxonomies/NetworkTaxonomyContentPanel";
import { NetworkTaxonomyData } from "@app/network-taxonomies/networkTaxonomy";
import { FeatureFlagContextProps, withFeatureFlagContext } from "@app/feature-flags/FeatureFlagContext";
import { Modal } from "../../shared/Modals";
import { cloneProjectThingsToResetIfZipProject, cloneThingsToResetIfZipProject } from "../helpers";
import { TemplateInfo } from "../network-assessment/EditTemplateOptionsHelper";
import ZipProjectService from "../zipProjectService";
import ResourceTaggingService from "@app/resource-tagging/resourceTaggingService";
import { ManageResourceTagDisplayModel, Perspectives } from "@app/tags/types";
import { ResourceTagModel } from "@app/tags/import/types";

import { QesIcon } from "@app/shared/icons/QESIcon";
import { QesIcons } from "@app/shared/icons/qesIcons";

export interface CloneProjectState {
  accuracyInsurancesIsLoading: boolean;
  accuracyTemplateCollections: AccuracyTemplateCollectionMetadata[];
  accuracySpecialtyDefinitions: AccuracySpecialtyDefinition[];
  allAccessCodeMapSets: CodeMapSet[];
  allMemberPopulations: MemberSetModel[];
  authorizedInsurances: ClientInsuranceModel[];
  cloneRunning: boolean;
  disableMarketProjectionCopy: boolean;
  disableProspectProjectionCopy: boolean;
  disableTerminationCopy: boolean;
  doesNetworkProviderHaveChange: boolean;
  error?: string | CloneProjectErrorTemplates;
  excludeDeactivatedFacilityImpact: boolean;
  excludeDeactivatedFacilityNetwork: boolean;
  excludeDeactivatedProviderImpact: boolean;
  excludeDeactivatedProviderNetwork: boolean;
  excludeInvalidNppesNpiFacilityImpact: boolean;
  excludeInvalidNppesNpiFacilityNetwork: boolean;
  excludeInvalidNppesNpiProviderImpact: boolean;
  excludeInvalidNppesNpiProviderNetwork: boolean;
  excludeMissingNpiFacilityImpact: boolean;
  excludeMissingNpiFacilityNetwork: boolean;
  excludeMissingNpiProviderImpact: boolean;
  excludeMissingNpiProviderNetwork: boolean;
  excludeMedicareOptOutImpact: boolean;
  excludeOIGExclusionImpact: boolean;
  excludePrecludedImpact: boolean;
  excludeMedicareOptOutNetwork: boolean;
  excludeOIGExclusionNetwork: boolean;
  excludePrecludedNetwork: boolean;
  filteredIncludedSpecialtyGroupExceptions: Array<{ specialtyGroupException: SpecialtyGroupExceptionDisplay; included: boolean }>;
  includedSpecialtyGroupExceptions: Array<{ specialtyGroupException: SpecialtyGroupExceptionDisplay; included: boolean }>;
  isIncludeSupplementaryDocumentsChecked: boolean;
  isNetworkTaxonomyValid: boolean;
  isZipProject: boolean;
  linkToProject: boolean;
  loading: boolean;
  hideClosestProviderReportOption: boolean;
  hideCMSApplicationCredit: boolean;
  hideCareJourneyScores: boolean;
  hideCreateStandards: boolean;
  hideEnterpriseComplianceDashboard: boolean;
  hideMotiveAppropriatenessScore: boolean;
  hideStrictTimeDistance: boolean;
  hideUseInMarketAnalysis: boolean;
  marketProjectionTypesToCopy: ProjectionProviderType[];
  marketProviderSets: ProviderSet[];
  modalRunNowMessageVisible: boolean;
  onCloneToZipProjectWarningSubmit: () => void;
  providerSets: ProviderSet[];
  project: Project;
  projectNames: string[];
  projectAccessCodeMapSets: CodeMapSet[];
  projectMemberPopulations: MemberSetModel[];
  prospectProjectionTypesToCopy: ProjectionProviderType[];
  resourceSharingUsers: ResourceSharingUserModel[];
  resourceTags: ManageResourceTagDisplayModel[],
  retainMarketCheckAllChecked: boolean;
  retainProspectCheckAllChecked: boolean;
  retainNetNewProviders: boolean;
  selectedInsurances: ClientInsuranceModel[];
  selectedInsuranceUid: string;
  selectedSpecialtyExceptionFilters: SpecialtyExceptionFilter[];
  selectedSpecialtyExceptionFilterType: SpecialtyExceptionFilterType;
  selectedSpecialtyExceptionFilterValue: string;
  shareUsersCheckAllChecked: boolean;
  showAffiliationsInsights: boolean;
  displayOnNetworkAccordion: boolean;
  showCloneToZipProjectWarning: boolean;
  showFilterBy: boolean;
  showNetNewProviders: boolean;
  showNetworkSpecialtyCodeMap: boolean;
  showPatternOfCareProviders: boolean;
  sortedSpecialtyGroups: TemplateSpecialtyGroup[];
  specialtyCodeMapSets: CodeMapSet[];
  specialtyExceptionCheckAllChecked: boolean;
  specialtyFilterValues: string[];
  specialtyToQdpCodeMapSets: CodeMapSet[];
  templateAccessGranularity: AccessCriteriaGranularityEnum;
  templateCollections: TemplateCollectionMetadata[];
  timeDistanceCalculatorTypes: TimeDistanceCalculatorType[];

  alternateMemberships: MemberSetModel[];
  disableMemberBindingTable: boolean;
  specialtyOverrides: SpecialtyOverrides[];
  specialtyGroups: TemplateSpecialtyGroup[];

  useAccuracy: boolean;
  useAdequacy: boolean;
}

enum SpecialtyExceptionFilterType {
  RegionDescription = "County Name", //TODO: need to handle zip codes
  Selected = "Selected",
  PreviouslyApproved = "Previously Approved",
  SpecialtyCode = "Specialty Code",
  StateName = "State Name",
  Status = "Status This Year",
}

enum CloneProjectErrorTemplates {
  Unexpected = "Unexpected",
}

class SpecialtyExceptionFilter {
  public specialtyExceptionFilterType: SpecialtyExceptionFilterType;
  public stringFilter: string;

  constructor(specialtyExceptionFilterType: SpecialtyExceptionFilterType, stringFilter: string) {
    this.specialtyExceptionFilterType = specialtyExceptionFilterType;
    this.stringFilter = stringFilter;
  }
}

type CloneProjectProps = RouteComponentProps<{ projectId: string }, unknown, { linkToProject: boolean }> & UserContextProps & FeatureFlagContextProps;

type UpdateResourceKeys = keyof Project;

class CloneProject extends React.Component<CloneProjectProps, CloneProjectState> {
  private readonly accuracyTemplateService = new AccuracyTemplateService();
  private readonly codeMapSetService = new CodeMapSetService();
  private filterValuesMap = new Map<SpecialtyExceptionFilterType, string[]>();
  private initialCloneName: string;
  private initialProjectName: string;
  private initialShowClosestProviderSetting: boolean;
  private initialNetworkProviderSetId: number;

  private readonly insuranceService = new InsuranceService();
  private readonly memberSetService = new MemberSetService();
  private readonly projectService = new ProjectService();
  private readonly zipProjectService = new ZipProjectService();
  private readonly providerSetService = new ProviderSetService();
  private readonly resourceService = new ResourceService();
  private readonly specialtyGroupExceptionsService = new SpecialtyGroupExceptionsService();
  private readonly templateService = new TemplateService();
  private readonly resourceTaggingService = new ResourceTaggingService();

  private readonly sortOrder: Array<keyof TemplateSpecialtyGroup> = ["displayOrder"];
  private originalMarketProviderSetId?: number;
  private originalProspectProviderSetId?: number;
  private adequacyTemplateCollectionIdRef = React.createRef<HTMLSelectElement>();

  constructor(props: CloneProjectProps) {
    super(props);
    this.state = {
      accuracyInsurancesIsLoading: false,
      accuracySpecialtyDefinitions: [],
      accuracyTemplateCollections: [],
      allAccessCodeMapSets: [],
      allMemberPopulations: null,
      alternateMemberships: [],
      authorizedInsurances: [],
      cloneRunning: false,
      disableMarketProjectionCopy: false,
      disableMemberBindingTable: true,
      disableProspectProjectionCopy: false,
      disableTerminationCopy: false,
      displayOnNetworkAccordion: false,
      doesNetworkProviderHaveChange: false,
      excludeDeactivatedFacilityImpact: null,
      excludeDeactivatedFacilityNetwork: null,
      excludeDeactivatedProviderImpact: null,
      excludeDeactivatedProviderNetwork: null,
      excludeInvalidNppesNpiFacilityImpact: null,
      excludeInvalidNppesNpiFacilityNetwork: null,
      excludeInvalidNppesNpiProviderImpact: null,
      excludeInvalidNppesNpiProviderNetwork: null,
      excludeMedicareOptOutImpact: null,
      excludeMedicareOptOutNetwork: null,
      excludeMissingNpiFacilityImpact: null,
      excludeMissingNpiFacilityNetwork: null,
      excludeMissingNpiProviderImpact: null,
      excludeMissingNpiProviderNetwork: null,
      excludeOIGExclusionImpact: null,
      excludeOIGExclusionNetwork: null,
      excludePrecludedImpact: null,
      excludePrecludedNetwork: null,
      filteredIncludedSpecialtyGroupExceptions: [],
      hideCMSApplicationCredit: true,
      hideCareJourneyScores: true,
      hideClosestProviderReportOption: false,
      hideCreateStandards: true,
      hideEnterpriseComplianceDashboard: true,
      hideMotiveAppropriatenessScore: true,
      hideStrictTimeDistance: false,
      hideUseInMarketAnalysis: true,
      includedSpecialtyGroupExceptions: [],
      isIncludeSupplementaryDocumentsChecked: false,
      isNetworkTaxonomyValid: true,
      isZipProject: false,
      linkToProject: props.location.state && (props.location.state).linkToProject || false,
      loading: true,
      marketProjectionTypesToCopy: [],
      marketProviderSets: [],
      modalRunNowMessageVisible: false,
      onCloneToZipProjectWarningSubmit: null,
      project: null,
      projectAccessCodeMapSets: [],
      projectMemberPopulations: null,
      projectNames: [],
      prospectProjectionTypesToCopy: [],
      providerSets: [],
      resourceSharingUsers: [],
      resourceTags: [],
      retainMarketCheckAllChecked: true,
      retainNetNewProviders: false,
      retainProspectCheckAllChecked: true,
      selectedInsuranceUid: "",
      selectedInsurances: [],
      selectedSpecialtyExceptionFilterType: SpecialtyExceptionFilterType.RegionDescription,
      selectedSpecialtyExceptionFilterValue: "",
      selectedSpecialtyExceptionFilters: [],
      shareUsersCheckAllChecked: false,
      showAffiliationsInsights: false,
      showCloneToZipProjectWarning: false,
      showFilterBy: false,
      showNetNewProviders: false,
      showNetworkSpecialtyCodeMap: false,
      showPatternOfCareProviders: false,
      sortedSpecialtyGroups: null,
      specialtyCodeMapSets: [],
      specialtyExceptionCheckAllChecked: true,
      specialtyFilterValues: [],
      specialtyGroups: [],
      specialtyOverrides: [],
      specialtyToQdpCodeMapSets: [],
      templateAccessGranularity: AccessCriteriaGranularityEnum.Unknown,
      templateCollections: [],
      timeDistanceCalculatorTypes: [],

      useAccuracy: false,
      useAdequacy: false,
    };
  }

  public async componentDidMount() {
    let haveAdequacy = false;
    const projectId = Number(this.props.match.params.projectId);
    const project: Project = await this.projectService.getById(projectId);

    const userIsResourceAdmin = this.props.user.roles.includes(Roles.CLIENT_ADMIN);
    const resourceTags = userIsResourceAdmin ? await this.resourceTaggingService.getClientProjectTagInfo(Perspectives.All) : [];

    const specialtyGroupExceptions =
      project.resource.resourceType === ResourceType.ZipAnalysisProject ?
        [] :
        await this.specialtyGroupExceptionsService.getSpecialtyGroupExceptionDisplays(projectId);
    const includedSpecialtyGroupExceptions =
      specialtyGroupExceptions.map((exception) => ({ included: true, specialtyGroupException: exception }));

    if (specialtyGroupExceptions.length > 0) {
      this.filterValuesMap.set(SpecialtyExceptionFilterType.RegionDescription, []);
      this.filterValuesMap.set(SpecialtyExceptionFilterType.Selected, ["True", "False"]);
      this.filterValuesMap.set(SpecialtyExceptionFilterType.PreviouslyApproved, ["True", "False"]);
      this.filterValuesMap.set(SpecialtyExceptionFilterType.SpecialtyCode, []);
      this.filterValuesMap.set(SpecialtyExceptionFilterType.StateName, []);
      this.filterValuesMap.set(SpecialtyExceptionFilterType.Status,
        [startCase(SpecialtyGroupExceptionStatus.Pending),
        startCase(SpecialtyGroupExceptionStatus.Submitted),
        startCase(SpecialtyGroupExceptionStatus.Approved),
        startCase(SpecialtyGroupExceptionStatus.Denied),
        startCase(SpecialtyGroupExceptionStatus.Appealed)]);
    }

    const resourceSharingUsers = await this.resourceService.getCloneShareUsers(projectId);

    const shareUsersCheckAllChecked =
      resourceSharingUsers.length > 0 && !resourceSharingUsers.some((user) => !user.shared || user.readOnly);

    const excludeDeactivatedFacilityImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedFacility);
    const excludeDeactivatedFacilityNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedFacility);
    const excludeDeactivatedProviderImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedProvider);
    const excludeDeactivatedProviderNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.DeactivatedProvider);
    const excludeInvalidNppesNpiFacilityImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiFacility);
    const excludeInvalidNppesNpiFacilityNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiFacility);
    const excludeInvalidNppesNpiProviderImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiProvider);
    const excludeInvalidNppesNpiProviderNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.InvalidNppesNpiProvider);
    const excludeMissingNpiFacilityImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiFacility);
    const excludeMissingNpiFacilityNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiFacility);
    const excludeMissingNpiProviderImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiProvider);
    const excludeMissingNpiProviderNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.MissingNpiProvider);
    const excludeMedicareOptOutNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.MedicareOptOut);
    const excludeOIGExclusionNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.OIGExclusion);
    const excludePrecludedNetwork =
      project.networkExclusions.some((exclusion) => exclusion === ProjectExclusionType.Precluded);
    const excludeMedicareOptOutImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.MedicareOptOut);
    const excludeOIGExclusionImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.OIGExclusion);
    const excludePrecludedImpact =
      project.impactExclusions.some((exclusion) => exclusion === ProjectExclusionType.Precluded);
    this.setState((prevState) => ({
      ...prevState,
      displayOnNetworkAccordion: project.displayOnNetworkAccordion,
      showAffiliationsInsights: project.showAffiliationsInsights,
    }));

    //change undefined properties to null so dirty checks work when an object is populated then unpopulated
    if (project.prospectProviderSetId === undefined) {
      project.prospectProviderSetId = null;
    }
    if (project.marketProviderSetId === undefined) {
      project.marketProviderSetId = null;
    }
    if (project.accessCodeMapSetId === undefined) {
      project.accessCodeMapSetId = null;
    }
    if (project.networkSpecialtyCodeMapSetId === undefined) {
      project.networkSpecialtyCodeMapSetId = null;
    }
    if (project.marketSpecialtyCodeMapSetId === undefined) {
      project.marketSpecialtyCodeMapSetId = null;
    }

    this.originalMarketProviderSetId = project.marketProviderSetId;
    this.originalProspectProviderSetId = project.prospectProviderSetId;

    const accuracyTemplateCollections = await this.accuracyTemplateService.getTemplateCollections();
    const accuracySpecialtyDefinitions = await this.accuracyTemplateService.getSpecialtyDefinitions();
    const templateCollections = await this.templateService.getUnarchivedTemplateCollections();
    const allMemberPopulations = (project.adequacyTemplateCollectionId && project.adequacyTemplateCorrelationId) ? await this.memberSetService.getActive(true, false) : [];
    let allAccessCodeMapSets = await this.codeMapSetService.get(CodeMapType.CountyClassMap);
    allAccessCodeMapSets =
      allAccessCodeMapSets.concat(await this.codeMapSetService.get(CodeMapType.ZipCodeClassMap));

    const specialtyCodeMapSets = await this.codeMapSetService.get(CodeMapType.SpecialtyCodeMap);
    const specialtyToQdpCodeMapSets = await this.codeMapSetService.get(CodeMapType.SpecialtyToQDPCodeMap);

    const marketProviderSets = await this.providerSetService.getMarketProviderSets();
    const providerSets = await this.providerSetService.getProviderSets();

    let templateInfo: TemplateInfo;
    let specialtyGroups: TemplateSpecialtyGroup[] = null;

    if (project.accuracyTemplateCollectionId && project.accuracyTemplateCorrelationId) {
      this.setState((prevState) => ({
        ...prevState,
        useAccuracy: true,
      }));
    }

    if (project.adequacyTemplateCollectionId && project.adequacyTemplateCorrelationId) {
      templateInfo = await this.getTemplateInfo(
        allAccessCodeMapSets,
        allMemberPopulations,
        templateCollections,
        project.adequacyTemplateCollectionId,
        project.adequacyTemplateCorrelationId);
      specialtyGroups =
        await this.templateService.getTemplateSpecialtyGroups(project.adequacyTemplateCollectionId, project.templateId);
      haveAdequacy = true;
      this.setState((prevState) => ({
        ...prevState,
        timeDistanceCalculatorTypes: templateInfo.timeDistanceCalculatorTypes,
        useAdequacy: true,
      }));
    }

    let sortedSpecialtyGroups: TemplateSpecialtyGroup[];

    if (templateInfo) {
      sortedSpecialtyGroups =
        templateInfo.specialtyGroups && sortBy(templateInfo.specialtyGroups, this.sortOrder);
    }

    //this is an accuracy-only project, get specialty groups
    if (project.accuracySpecialtyDefinitionId) {
      sortedSpecialtyGroups = await this.getAccuracySpecialtyGroups(project.accuracySpecialtyDefinitionId);
      specialtyGroups = await this.getAccuracySpecialtyGroups(project.accuracySpecialtyDefinitionId);
    }

    const hasCmsApplicationCredits: boolean = sortedSpecialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);

    const showMotiveAppropriatenessScore = !!(templateInfo?.motiveCrosswalkCodeMapSetId);
    const showUseInMarketAnalysis = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
    const showEnterpriseComplianceDashboard = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
    const hideStrictTimeDistance = this.props.enabledFeatureFlags.has(Features.HIDE_STRICT_TIME_DISTANCE);
    const showNetworkSpecialtyCodeMap = this.props.enabledFeatureFlags.has(Features.NETWORK_BENCHMARKING) && this.props.user.roles.includes(Roles.COMPETITIVE_INSIGHTS_USER);
    const showNetNewProviders = this.props.user.roles.includes(Roles.NET_NEW_PROVIDER_USER);
    const hideStandardSettingsCreation = !(this.props.enabledFeatureFlags.has(Features.STANDARD_SETTING_CREATION) && this.props.user.roles.includes(Roles.STANDARDS_CREATOR_USER));
    const showPatternOfCareProviders = this.props.user.roles.includes(Roles.PATTERN_OF_CARE_USER) && project.showPatternOfCareProviders;
    const showCareJourneyScores = this.props.enabledFeatureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES);

    this.initialProjectName = project.resource.name;
    this.initialShowClosestProviderSetting = project.showClosestProviderReport;
    this.initialCloneName = `${project.resource.name} - CLONE`;
    project.resource.name = this.initialCloneName;

    this.initialNetworkProviderSetId = project.networkProviderSetId;

    const disableMarketProjectionCopy = !this.originalMarketProviderSetId;
    const marketProjectionTypesToCopy = disableMarketProjectionCopy ? [] : [ProjectionProviderType.Target, ProjectionProviderType.ExcludeImpact];
    const retainMarketCheckAllChecked = !disableMarketProjectionCopy;
    const disableProspectProjectionCopy = !this.originalProspectProviderSetId;
    const prospectProjectionTypesToCopy = disableProspectProjectionCopy ? [] : [ProjectionProviderType.Target, ProjectionProviderType.ExcludeImpact];
    const retainProspectCheckAllChecked = !disableProspectProjectionCopy;
    const projectNames = await this.resourceService.getProjectNames();

    const isZipTemplate = templateCollections.find((tc) => tc.templateCollectionId === project.adequacyTemplateCollectionId)?.resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
    if (isZipTemplate) {
      project.excludeInVolumeActions = true;
      project.strictTimeAndDistance = true;
    }

    let sortedAuthorizedInsurances: ClientInsuranceModel[] = [];
    let selectedInsurances: ClientInsuranceModel[] = [];

    //this project includes accuracy. Determine selected insurances, if any
    if (project && project.accuracyTemplateCollectionId) {
      let error = this.state.error;

      try {
        this.setState({ accuracyInsurancesIsLoading: true });
        const authorizedInsurances = await this.insuranceService.getAuthorizedInsurancesByClientId();
        sortedAuthorizedInsurances = authorizedInsurances && sortBy(authorizedInsurances, "planName");
      }
      catch {
        error = "Unable to load available accuracy insurance plans at this time.";
      }

      if (this.state.accuracyInsurancesIsLoading || error) {
        this.setState({
          accuracyInsurancesIsLoading: false,
          error: error
        });
      }

      if (project.inspectedInsurancesList) {
        selectedInsurances = sortedAuthorizedInsurances.filter((ins) => (project.inspectedInsurancesList.findIndex((lookup) => lookup.uid === ins.uid) > -1));
      }
    }

    this.setState({
      accuracySpecialtyDefinitions,
      accuracyTemplateCollections,
      allAccessCodeMapSets,
      allMemberPopulations,
      authorizedInsurances: sortedAuthorizedInsurances,
      disableMarketProjectionCopy,
      disableProspectProjectionCopy,
      excludeDeactivatedFacilityImpact,
      excludeDeactivatedFacilityNetwork,
      excludeDeactivatedProviderImpact,
      excludeDeactivatedProviderNetwork,
      excludeInvalidNppesNpiFacilityImpact,
      excludeInvalidNppesNpiFacilityNetwork,
      excludeInvalidNppesNpiProviderImpact,
      excludeInvalidNppesNpiProviderNetwork,
      excludeMedicareOptOutImpact,
      excludeMedicareOptOutNetwork,
      excludeMissingNpiFacilityImpact,
      excludeMissingNpiFacilityNetwork,
      excludeMissingNpiProviderImpact,
      excludeMissingNpiProviderNetwork,
      excludeOIGExclusionImpact,
      excludeOIGExclusionNetwork,
      excludePrecludedImpact,
      excludePrecludedNetwork,
      filteredIncludedSpecialtyGroupExceptions: includedSpecialtyGroupExceptions,
      hideCMSApplicationCredit: !hasCmsApplicationCredits,
      hideCareJourneyScores: !showCareJourneyScores,
      hideCreateStandards: hideStandardSettingsCreation,
      hideEnterpriseComplianceDashboard: !showEnterpriseComplianceDashboard,
      hideMotiveAppropriatenessScore: !showMotiveAppropriatenessScore,
      hideStrictTimeDistance: hideStrictTimeDistance,
      hideUseInMarketAnalysis: !showUseInMarketAnalysis,
      includedSpecialtyGroupExceptions,
      isZipProject: isZipTemplate,
      loading: false,
      marketProjectionTypesToCopy,
      marketProviderSets,
      project,
      projectAccessCodeMapSets: templateInfo ? templateInfo.projectAccessCodeMapSets : null,
      projectMemberPopulations: templateInfo ? templateInfo.projectMemberPopulations : null,
      projectNames,
      prospectProjectionTypesToCopy,
      providerSets,
      resourceSharingUsers,
      resourceTags,
      retainMarketCheckAllChecked,
      retainProspectCheckAllChecked,
      selectedInsurances,
      shareUsersCheckAllChecked,
      showNetNewProviders: showNetNewProviders,
      showNetworkSpecialtyCodeMap: showNetworkSpecialtyCodeMap,
      showPatternOfCareProviders: showPatternOfCareProviders,
      sortedSpecialtyGroups,
      specialtyCodeMapSets,
      specialtyGroups,
      specialtyToQdpCodeMapSets,
      templateAccessGranularity: templateInfo ? templateInfo.templateAccessGranularity : null,
      templateCollections,
    }, () => {
      //Populating membership option tables
      if (haveAdequacy) {
        this.populateOverride();
      } else {
        this.freshOverrides();
      }
      this.generateAltMemberships();
      this.generateSpecialtyOverrides(specialtyGroups);
      this.toggleMemberBinding();
      this.toggleShowClosestProviderReportOption();
    });
  }

  public async componentDidUpdate(prevProps: CloneProjectProps, prevState: CloneProjectState) {
    if (this.state.error) {
      return;
    }

    //lodash isEqual does not deep compare arrays of objects so get an array of exception filter differences to determine if there are any changes
    const exceptionFilterChangeArray = differenceWith(this.state.selectedSpecialtyExceptionFilters,
      prevState.selectedSpecialtyExceptionFilters,
      isEqual);

    //also need to update the filters if the includedExceptions changed
    const includedSpecialtyExceptionArray = differenceWith(this.state.includedSpecialtyGroupExceptions,
      prevState.includedSpecialtyGroupExceptions,
      isEqual);

    if (this.state.selectedSpecialtyExceptionFilters.length !== prevState.selectedSpecialtyExceptionFilters.length ||
      exceptionFilterChangeArray.length > 0 ||
      includedSpecialtyExceptionArray.length > 0) {
      let filteredIncludedSpecialtyGroupExceptions = this.state.includedSpecialtyGroupExceptions;

      for (const filter of this.state.selectedSpecialtyExceptionFilters) {
        switch (filter.specialtyExceptionFilterType) {
          case SpecialtyExceptionFilterType.PreviouslyApproved:
            filteredIncludedSpecialtyGroupExceptions =
              filteredIncludedSpecialtyGroupExceptions.filter(
                (se) => se.specialtyGroupException.previouslyApproved === (filter.stringFilter === "True"));
            break;

          case SpecialtyExceptionFilterType.Selected:
            filteredIncludedSpecialtyGroupExceptions =
              filteredIncludedSpecialtyGroupExceptions.filter(
                (se) => se.included === (filter.stringFilter === "True"));
            break;

          case SpecialtyExceptionFilterType.RegionDescription:
            filteredIncludedSpecialtyGroupExceptions =
              filteredIncludedSpecialtyGroupExceptions.filter(
                (se) => se.specialtyGroupException.regionSetDescription.toLowerCase().indexOf(filter.stringFilter.toLowerCase()) > -1);
            break;

          case SpecialtyExceptionFilterType.SpecialtyCode:
            filteredIncludedSpecialtyGroupExceptions =
              filteredIncludedSpecialtyGroupExceptions.filter(
                (se) => se.specialtyGroupException.specialtyGroupCode.toLowerCase().indexOf(filter.stringFilter.toLowerCase()) > -1);
            break;

          case SpecialtyExceptionFilterType.StateName:
            filteredIncludedSpecialtyGroupExceptions =
              filteredIncludedSpecialtyGroupExceptions.filter(
                (se) => se.specialtyGroupException.regionSetStateName.toLowerCase().indexOf(filter.stringFilter.toLowerCase()) > -1);
            break;

          case SpecialtyExceptionFilterType.Status:
            filteredIncludedSpecialtyGroupExceptions =
              filteredIncludedSpecialtyGroupExceptions.filter(
                (se) => filter.stringFilter === startCase(se.specialtyGroupException.specialtyGroupExceptionStatus));
            break;
        }
      }

      const includedSpecialtyGroupCount = filteredIncludedSpecialtyGroupExceptions.filter((includedSpecialtyGroup) => includedSpecialtyGroup.included).length;
      const specialtyExceptionCheckAllChecked = (includedSpecialtyGroupCount > 0 && includedSpecialtyGroupCount === filteredIncludedSpecialtyGroupExceptions.length);

      this.setState({
        filteredIncludedSpecialtyGroupExceptions,
        specialtyExceptionCheckAllChecked,
      });
    }

    if (prevProps.enabledFeatureFlags !== this.props.enabledFeatureFlags) {
      const showUseInMarketAnalysis = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
      const showEnterpriseComplianceDashboard = this.props.user.roles.includes(Roles.ENTERPRISE_COMPLIANCE_DASHBOARD_USER);
      const showNetworkSpecialtyCodeMap = this.props.enabledFeatureFlags.has(Features.NETWORK_BENCHMARKING) && this.props.user.roles.includes(Roles.COMPETITIVE_INSIGHTS_USER);
      const showNetNewProviders = this.props.user.roles.includes(Roles.NET_NEW_PROVIDER_USER);
      const showCareJourneyScores = this.props.enabledFeatureFlags.has(Features.ENABLE_CARE_JOURNEY_SCORES);

      let showMotiveAppropriatenessScore = false;

      if (this.state.project?.adequacyTemplateCollectionId !== null && this.state.project?.adequacyTemplateCorrelationId !== null) {
        const templateInfo = await this.templateService.getTemplateById(this.state.project.adequacyTemplateCollectionId, this.state.project.adequacyTemplateCorrelationId, null);
        showMotiveAppropriatenessScore = templateInfo.motiveCrosswalkCodeMapSetId !== undefined && templateInfo.motiveCrosswalkCodeMapSetId !== null;
      }

      this.setState({
        hideCareJourneyScores: !showCareJourneyScores,
        hideEnterpriseComplianceDashboard: !showEnterpriseComplianceDashboard,
        hideMotiveAppropriatenessScore: !showMotiveAppropriatenessScore,
        hideUseInMarketAnalysis: !showUseInMarketAnalysis,
        showNetNewProviders: showNetNewProviders,
        showNetworkSpecialtyCodeMap: showNetworkSpecialtyCodeMap
      });
    }

    if (prevState.project?.adequacyTemplateCollectionId !== this.state.project?.adequacyTemplateCollectionId && this.state.useAdequacy && this.state.templateCollections.length > 0) {
      const isZipTemplateCollection = this.state.templateCollections.find((tc) => tc.templateCollectionId === this.state.project.adequacyTemplateCollectionId)
        ?.resource.resourceType === ResourceType.AdequacyZipTemplateCollection;

      this.setState({
        isZipProject: isZipTemplateCollection,
      })
    }
  }

  public render() {
    const breadcrumb = this.state.linkToProject ?
      this.state.project ? this.state.project.resource.name : "Loading..." :
      "Projects";
    const link = this.state.linkToProject ?
      this.state.project ? `/projects/${this.state.project && this.state.project.projectId}` : "" :
      "";
    return (
      <Page documentTitle="Clone Project">
        <Breadcrumbs
          items={[
            { link, text: breadcrumb },
            "Clone Project",
          ]}
        />
        <PageInner>
          <Modal
            closeModal={() => {
              this.setState({
                onCloneToZipProjectWarningSubmit: null,
                showCloneToZipProjectWarning: false,
              });
            }}
            isVisible={this.state.showCloneToZipProjectWarning}
            content={{
              body: "Changing the project to use a Zip Code template will limit the available Project Options. Please check the available project options, and select those that may apply.",
              cancelButtonText: "Cancel",
              confirmButtonAction: this.state.onCloneToZipProjectWarningSubmit,
              confirmButtonText: "Acknowledge",
              title: "Adequacy Template Collection Changed from County to Zip Level Analysis",
            }}
            triggerElement={this.adequacyTemplateCollectionIdRef}
          />
          <PageHeader title="Clone Project">
            <PageHeaderLeft>
              <a className="d-none d-md-inline" data-toggle="collapse" href="#cloneInformation" role="button">
                <QesIcon iconName={QesIcons.FontAwesome.InfoCircle} description="click for more information" width="13" classNames="align-baseline" />
              </a>
            </PageHeaderLeft>
            <PageHeaderRight>
              <div className="dropdown">
                <QESButton qesTestId="clone-project_create-clone" qesType="primary" disabled={this.isCloneDisabled() || !!this.state.error} id="cloneDropdownButton" data-toggle="dropdown" aria-haspopup="true" aria-expanded="false">
                  {(this.state.cloneRunning || null) && <><QesIcon iconName={QesIcons.FontAwesome.Spinner} classNames="qes-icon-spin align-text-top" description="loading" />&nbsp;</>}
                  {(!this.state.cloneRunning || null) && <><QesIcon iconName={QesIcons.FontAwesome.ChevronDown} classNames="align-baseline" width="14" isDecorative />&nbsp;</>}
                  Create Clone
                </QESButton>
                <ul className="dropdown-menu dropdown-menu-consistent-width">
                  <li><a href="#" className="fs12-imp dropdown-item" onClick={() => this.handleCloneRun(true)}>Run Now</a></li>
                  <li><a href="#" className="fs12-imp dropdown-item" onClick={() => this.handleCloneRun(false)}>Run Later</a></li>
                </ul>
              </div>
            </PageHeaderRight>
          </PageHeader>
          <PageSection>
            <div className="collapse" id="cloneInformation">
              <small>
                <QesIcon iconName={QesIcons.FontAwesome.InfoCircle} classNames="mr-2" width="10.5" isDecorative />
                All of the details (other than the name) from the original project have been automatically filled out below.
                Any changes made here will not affect the original project. For example, you can:<br />
                <QesIcon iconName={QesIcons.FontAwesome.CaretRight} classNames="ml-4" width="4.5" isDecorative /> Fill out the &apos;Name&apos; field then click &apos;Create Clone&apos; to create an exact copy<br />
                <QesIcon iconName={QesIcons.FontAwesome.CaretRight} classNames="ml-4" width="4.5" isDecorative /> Modify as many aspects as you would like then click &apos;Create Clone&apos; to create a copy with the modifications<br />
                <a data-toggle="collapse" href="#cloneInformation" role="button">hide</a>
              </small>
            </div>
          </PageSection>
          {this.renderBody()}
        </PageInner>
      </Page>
    );
  }

  private renderErrorFragment = (error: string | CloneProjectErrorTemplates) => {
      switch (error) {
        case CloneProjectErrorTemplates.Unexpected:
          return <><strong>Uh oh! </strong>An unexpected error occurred. If the issue persists, please contact your administrator.</>
        default:
          return <>{error}</>
      }
  }

  private renderBody = () => {
    if (this.state.error) {
      return (
        <PageSection>
          <div className="row">
            <div className="col-12">
              <QESAlert type="danger" classNames="mt0">
                {this.renderErrorFragment(this.state.error)}
              </QESAlert>
            </div>
          </div>
        </PageSection>
      );
    }

    if (this.state.loading) {
      return (
        <div className="mt-3">
          <LoadingSpinner />
        </div>
      );
    }

    const projectNameOk = this.state.project.resource.name === this.initialCloneName || (this.state.project.resource.name !== null && this.state.project.resource.name !== "" && !this.state.projectNames.includes(this.state.project.resource.name));
    const projectDescriptionOk = (this.state.project.resource.description !== null && this.state.project.resource.description !== "");

    return (
      <PageSection>
        <div className="row">
          <div className="col-md-12 mt30">
            <p>This project will be a clone of <strong>{this.initialProjectName}</strong>.</p>
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={"Details"}
                testId="clone-project__details-accordion"
              >
                <div className="form-horizontal">
                  <Details
                    disabled={false}
                    onDescChange={this.handleDescChange}
                    onIsPrivateChange={this.handleIsPrivateChange}
                    onNameChange={this.handleNameChange}
                    onTaggingChange={this.handleTagggingChange}
                    project={this.state.project}
                    projectDescriptionOk={projectDescriptionOk}
                    projectNameOk={projectNameOk}
                    projectNameValue={this.state.project.resource.name}
                    resourceTags={this.state.resourceTags}
                  />
                </div>
              </Accordion>
            </AccordionContainer>
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={"Template"}
                testId="clone-project__template-accordion"
              >
                <UserContext.Consumer>
                  {(user) => (
                    <TemplateOptions
                      accuracyInsurancesIsLoading={this.state.accuracyInsurancesIsLoading}
                      accuracyTemplateCollections={this.state.accuracyTemplateCollections}
                      accuracySpecialtyDefinitions={this.state.accuracySpecialtyDefinitions}
                      adequacyTemplateCollectionIdRef={this.adequacyTemplateCollectionIdRef}
                      authorizedInsurances={this.state.authorizedInsurances}
                      checkboxClass={"checkbox"}
                      disabled={false}
                      handleAddInspectedInsurance={this.handleAddInspectedInsurance}
                      handleAuthorizedInsurancesChange={this.handleAuthorizedInsurancesChange}
                      handleRemoveInspectedInsurance={this.handleRemoveInspectedInsurance}
                      handleUseAccuracyChange={this.handleUseAccuracyChange}
                      handleUseAdequacyChange={this.handleUseAdequacyChange}
                      onAccessCodeMapSetChange={this.handleAccessCodeMapSetChange}
                      onAccuracySpecialtyDefinitionIdChange={this.handleAccuracySpecialtyDefinitionIdChange}
                      onAccuracySpecialtyToQdpCodeMapChange={this.handleAccuracySpecialtyToQdpCodeMapChange}
                      onAccuracyTemplateCollectionChange={this.handleAccuracyTemplateCollectionChange}
                      onAccuracyTemplateCorrelationIdChange={this.handleAccuracyTemplateCorrelationIdChange}
                      onCheckBoxChange={this.handleCheckBoxChange}
                      onMarketChanged={this.handleMarketChanged}
                      onMarketSpecialtyCodeMapChange={this.handleMarketSpecialtyCodeMapChange}
                      onNetworkSpecialtyCodeMapChange={this.handleNetworkSpecialtyCodeMapChange}
                      onSpecialtyGroupsChange={this.handleSpecialtyGroupsChange}
                      onTelehealthSpecialtyGroupsChange={this.handleTelehealthSpecialtyGroupChange}
                      onTemplateCollectionChange={this.handleTemplateCollectionChange}
                      onTemplateCorrelationIdChange={this.handleTemplateCorrelationIdChange}
                      onTimeDistanceCalculatorTypeChange={this.handleTimeDistanceCalculatorTypeChange}
                      perspective={EditPerspective.Clone}
                      project={this.state.project}
                      projectAccessCodeMapSets={this.state.projectAccessCodeMapSets}
                      selectedInsuranceUid={this.state.selectedInsuranceUid}
                      selectedInsurances={this.state.selectedInsurances}
                      shared={false}
                      showNetworkSpecialtyCodeMap={this.state.showNetworkSpecialtyCodeMap}
                      sortedSpecialtyGroups={this.state.sortedSpecialtyGroups}
                      specialtyCodeMapSets={this.state.specialtyCodeMapSets}
                      specialtyToQdpCodeMapSets={this.state.specialtyToQdpCodeMapSets}
                      templateCollections={this.state.templateCollections}
                      templateAccessGranularity={this.state.templateAccessGranularity}
                      timeDistanceCalculatorTypes={this.state.timeDistanceCalculatorTypes}
                      useAccuracy={this.state.useAccuracy}
                      useAdequacy={this.state.useAdequacy}
                      user={user}
                      isZipProject={this.state.isZipProject}
                    />
                  )}
                </UserContext.Consumer>
              </Accordion>
            </AccordionContainer>
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={"Members"}
                testId="clone-project__members-accordion"
              >
                <MemberOptions
                  {...this.props}
                  checkboxClass={"checkbox"}
                  disabled={false}
                  generateAlternateMemberships={this.generateAltMemberships}
                  handleMemberPopAliasChange={this.handleMemberPopAliasChange}
                  handleMemberPopAliasChange2={this.handleMemberPopAliasChange2}
                  handleMemberPopChange={this.handleMemberPopChange}
                  handleMemberPopChange2={this.handleMemberPopChange2}
                  handleRiskPopChange={this.handleRiskPopChanged}
                  onAddAlternateRow={this.handleNewAlternativeMemberPop}
                  onAlternateMemberPopAliasChange={this.handleAlternateMemberPopAliasChange}
                  onAlternateMemberPopChange={this.handleAlternateMemberPopChange}
                  onCheckBoxChange={this.handleCheckBoxChange}
                  onDeleteAlternateRow={this.handleDeleteAlternateRow}
                  onSpecialtyGroupsChange={this.handleSpecialtyGroupsChange}
                  project={this.state.project}
                  projectMemberPopulations={this.state.projectMemberPopulations}
                  shared={false}
                  sortedSpecialtyGroups={this.state.sortedSpecialtyGroups}
                  isZipProject={this.state.isZipProject}

                  allMemberPopulations={this.state.allMemberPopulations}
                  alternateMemberships={this.state.alternateMemberships}
                  disableMemberBindingTable={this.state.disableMemberBindingTable}
                  handlePrimaryMembershipBindingChange={this.handlePrimaryMembershipBindingChange}
                  handleSecondaryMembershipBindingChange={this.handleSecondaryMembershipBindingChange}
                  setPrimarySpecialtyOverride={this.setPrimarySpecialtyOverride}
                  setSecondarySpecialtyOverride={this.setSecondarySpecialtyOverride}
                  specialtyGroups={this.state.specialtyGroups}
                  specialtyOverrides={this.state.specialtyOverrides}
                />
              </Accordion>
            </AccordionContainer>
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={"Providers"}
                testId="clone-project__providers-accordion"
              >
                <ProviderOptions
                  enabledFeatureFlags={this.props.enabledFeatureFlags}
                  checkboxClass={"checkbox"}
                  disabled={false}
                  hideUseInMarketAnalysis={this.state.hideUseInMarketAnalysis}
                  marketProviderSets={this.state.marketProviderSets}
                  onCheckBoxChange={this.handleCheckBoxChange}
                  onMarketChanged={this.handleMarketChanged}
                  onProspectsChanged={this.handleProspectsChanged}
                  onProvidersChanged={this.handleProvidersChanged}
                  providerSets={this.state.providerSets}
                  project={this.state.project}
                  shared={false}
                  networkDisabled={false}
                  hideUseMarketProvidersForImpact={false}
                  onChangeMarketAlias={this.handleMarketProviderAliasChanged}
                  onChangeNetworkAlias={this.handleNetworkProviderAliasChanged}
                  onChangeProspectAlias={this.handleProspectProviderAliasChanged}
                  isZipProject={this.state.isZipProject}
                />
              </Accordion>
            </AccordionContainer>
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={"Network Taxonomy"}
                testId="clone-project__network-taxonomy-accordion"
              >
                <div className="tab-contents col-12">
                  <div className="col-10 offset-2">
                    <NetworkTaxonomyContentPanel
                      networkTaxonomyData={this.state.project.networkTaxonomy}
                      onNetworkTaxonomyChange={this.handleNetworkTaxonomyChange}
                      disabled={false}
                    />
                  </div>
                </div>
              </Accordion>
            </AccordionContainer>
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={"Options"}
                testId="clone-project__options-accordion"
              >
                <EditDisplayOptions
                  accuracyAndAdequacy={this.state.useAccuracy && this.state.useAdequacy}
                  checkboxClass={"checkbox"}
                  disabled={false}
                  disableUnsupportedOptions={false}
                  excludeDeactivatedFacilitiesImpact={this.state.excludeDeactivatedFacilityImpact}
                  excludeDeactivatedFacilitiesNetwork={this.state.excludeDeactivatedFacilityNetwork}
                  excludeDeactivatedProvidersImpact={this.state.excludeDeactivatedProviderImpact}
                  excludeDeactivatedProvidersNetwork={this.state.excludeDeactivatedProviderNetwork}
                  excludeInvalidNppesNpiFacilitiesImpact={this.state.excludeInvalidNppesNpiFacilityImpact}
                  excludeInvalidNppesNpiFacilitiesNetwork={this.state.excludeInvalidNppesNpiFacilityNetwork}
                  excludeInvalidNppesNpiProvidersImpact={this.state.excludeInvalidNppesNpiProviderImpact}
                  excludeInvalidNppesNpiProvidersNetwork={this.state.excludeInvalidNppesNpiProviderNetwork}
                  excludeMedicareOptOutProvidersImpact={this.state.excludeMedicareOptOutImpact}
                  excludeMedicareOptOutProvidersNetwork={this.state.excludeMedicareOptOutNetwork}
                  excludeMissingNpiFacilitiesImpact={this.state.excludeMissingNpiFacilityImpact}
                  excludeMissingNpiFacilitiesNetwork={this.state.excludeMissingNpiFacilityNetwork}
                  excludeMissingNpiProvidersImpact={this.state.excludeMissingNpiProviderImpact}
                  excludeMissingNpiProvidersNetwork={this.state.excludeMissingNpiProviderNetwork}
                  excludeOIGExcludedProvidersImpact={this.state.excludeOIGExclusionImpact}
                  excludeOIGExcludedProvidersNetwork={this.state.excludeOIGExclusionNetwork}
                  excludePrecludedProvidersImpact={this.state.excludePrecludedImpact}
                  excludePrecludedProvidersNetwork={this.state.excludePrecludedNetwork}
                  onCheckBoxChange={this.handleCheckBoxChange}
                  onImpactExclusionsChange={this.handleImpactExclusionsChange}
                  onNetworkExclusionsChange={this.handleNetworkExclusionsChange}
                  project={this.state.project}
                  shared={false}
                  hideClosestProviderReportOption={this.state.hideClosestProviderReportOption}
                  hideCMSApplicationCredit={this.state.hideCMSApplicationCredit}
                  hideCareJourneyScores={this.state.hideCareJourneyScores}
                  hideCreateStandards={this.state.hideCreateStandards}
                  hideMotiveAppropriatenessScore={this.state.hideMotiveAppropriatenessScore}
                  hideEnterpriseComplianceDashboard={this.state.hideEnterpriseComplianceDashboard}
                  hidePracAtLocCondifence={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PRAC_AT_LOC_CONFIDENCE)}
                  hideProviderClaimsActivity={!this.props.enabledFeatureFlags.has(Features.ACCURACY_ENABLE_PROVIDER_CLAIMS_ACTIVITY)}
                  useAccuracy={this.state.useAccuracy}
                  useAdequacy={this.state.useAdequacy}
                  hideStrictTimeDistance={this.state.hideStrictTimeDistance}
                  isZipProject={this.state.isZipProject}
                  showNetworkIntegrityDashboard={this.props.enabledFeatureFlags.has(Features.ENABLE_NETWORK_INTEGRITY_DASHBOARD)}
                  showAffiliationsInsights={this.state.showAffiliationsInsights}
                  displayOnNetworkAccordion={this.state.displayOnNetworkAccordion}
                />
              </Accordion>
            </AccordionContainer>
            {!this.state.isZipProject && (
              <>
                <RoleRestricted allowedRoles={[Roles.TERM_PROVIDER_ADMIN]}>
                  <AccordionContainer>
                    <Accordion
                      defaultOpen={false}
                      title={"Termed Providers"}
                      testId="clone-project__termed-providers-accordion"
                    >
                      {this.renderTermedProviderPanelContents()}
                    </Accordion>
                  </AccordionContainer>
                </RoleRestricted>
                <AccordionContainer>
                  <Accordion
                    defaultOpen={false}
                    title={"Targeted and Excluded Providers"}
                    testId="clone-project__targeted-and-excluded-providers-accordion"
                  >
                    {this.renderTargetedAndExcludedPanelContents()}
                  </Accordion>
                </AccordionContainer>
                <AccordionContainer>
                  <Accordion
                    defaultOpen={false}
                    title={"Specialty Exceptions"}
                    testId="clone-project__specialty-exceptions-accordion"
                  >
                    {this.renderSpecialtyExceptionPanelContents()}
                  </Accordion>
                </AccordionContainer>
              </>
            )}
            <AccordionContainer>
              <Accordion
                defaultOpen={false}
                title={"Sharing"}
                testId="clone-project__sharing-accordion"
              >
                {this.renderShareUsersPanelContents()}
              </Accordion>
            </AccordionContainer>
            {this.state.showNetNewProviders &&
              <RoleRestricted allowedRoles={[Roles.VOLUME_TARGET]}>
                <AccordionContainer>
                  <Accordion
                    defaultOpen={false}
                    title={"Net New Servicing Providers"}
                    testId="clone-project__new-new-servicing-providers-accordion"
                  >
                    {this.renderNetNewProviderPanelContents()}
                  </Accordion>
                </AccordionContainer>
              </RoleRestricted>
            }
          </div>
        </div>
      </PageSection>
    );
  }

  private renderTermedProviderPanelContents = () => (
    <QESCheckbox
      containerClassName="col-12 form-check form-check-inline"
      label="Copy Termed Providers"
      checked={!this.state.disableTerminationCopy}
      className="form-check-input"
      id="termed"
      onChange={this.toggleTermedProviders}
      testId="clone-project__copy-termed-providers-checkbox"
    />
  )

  private renderTargetedAndExcludedPanelContents = () => {
    return (
      <div className="row">
        <div className="col-md-12">
          <div className="container">
            <div className="row">
              <div className={`offset-lg-1 col-lg-4 col-md-6 col-sm-12${this.state.disableProspectProjectionCopy ? " text-muted" : ""}`}>
                <QESCheckbox
                  containerClassName="col-md-12 pl5 pt2 btrl2 border-info bg-info m-0 text-"
                  label="Prospect"
                  className="mr5-imp"
                  onChange={(e) => this.checkAllRetainProspectClick(e.target.checked)}
                  indeterminate={this.state.prospectProjectionTypesToCopy.length === 1}
                  checked={this.state.retainProspectCheckAllChecked}
                  disabled={this.state.disableProspectProjectionCopy}
                  testId="clone-project__check-all-retain-prospect-checkbox"
                />
                <div className="col-sm-12 pl5 brl2 border-info">
                  <QESCheckbox
                    containerClassName="pt10 fw-normal m-0"
                    label={<>Retain <strong>targeted</strong> providers</>}
                    onChange={(e) => this.retainProspectClick(e.target.checked, ProjectionProviderType.Target)}
                    checked={this.state.prospectProjectionTypesToCopy.indexOf(ProjectionProviderType.Target) > -1}
                    disabled={this.state.disableProspectProjectionCopy}
                    testId="clone-project__retain-targeted-prospect-providers-checkbox"
                  />
                </div>
                <div className="col-sm-12 pl5 brbl2 border-info">
                  <QESCheckbox
                    containerClassName="pt10 pb5 fw-normal m-0"
                    label={<>Retain <strong> excluded</strong> providers</>}
                    onChange={(e) => this.retainProspectClick(e.target.checked, ProjectionProviderType.ExcludeImpact)}
                    checked={this.state.prospectProjectionTypesToCopy.indexOf(ProjectionProviderType.ExcludeImpact) > -1}
                    disabled={this.state.disableProspectProjectionCopy}
                    testId="clone-project__retain-excluded-prospect-checkbox"
                  />
                </div>
                <div className="d-md-none d-lg-none d-xl-none pl5 col-sm-12 mt-20" />
              </div>
              <div className={`offset-lg-2 col-lg-4 col-md-6 col-xs-12${this.state.disableMarketProjectionCopy ? " text-muted" : ""}`}>
                <QESCheckbox
                  containerClassName="col-md-12 pl5 btrl2 border-info bg-info m-0"
                  label="Market"
                  className="mr5-imp"
                  onChange={(e) => this.checkAllRetainMarketClick(e.target.checked)}
                  indeterminate={this.state.marketProjectionTypesToCopy.length === 1}
                  checked={this.state.retainMarketCheckAllChecked}
                  disabled={this.state.disableMarketProjectionCopy}
                  testId="clone-project__check-all-retain-market-checkbox"
                />
                <div className="col-sm-12 pl5 brl2 border-info">
                  <QESCheckbox
                    containerClassName="pt10 m-0"
                    label={<>Retain <strong> targeted</strong> providers</>}
                    onChange={(e) => this.retainMarketClick(e.target.checked, ProjectionProviderType.Target)}
                    checked={this.state.marketProjectionTypesToCopy.indexOf(ProjectionProviderType.Target) > -1}
                    disabled={this.state.disableMarketProjectionCopy}
                    testId="clone-project__retain-targeted-market-providers-checkbox"
                  />
                </div>
                <div className="col-sm-12 pl5 brbl2 border-info">
                  <QESCheckbox
                    containerClassName="pt10 pb5 m-0"
                    label={<>Retain <strong> excluded</strong> providers</>}
                    onChange={(e) => this.retainMarketClick(e.target.checked, ProjectionProviderType.ExcludeImpact)}
                    checked={this.state.marketProjectionTypesToCopy.indexOf(ProjectionProviderType.ExcludeImpact) > -1}
                    disabled={this.state.disableMarketProjectionCopy}
                    testId="clone-project__retain-excluded-market-providers-checkbox"
                  />
                </div>
              </div>
              <div className=" col-md-12 mt35 clearfix" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  private renderSpecialtyExceptionPanelContents = () => {
    if (this.state.includedSpecialtyGroupExceptions.length === 0) {
      return (
        <div className="row">
          <div className="col-sm-12 text-center text-muted">
            There are no specialty exceptions for this project
          </div>
        </div>
      );
    }

    if (this.state.filteredIncludedSpecialtyGroupExceptions.length === 0) {
      return this.renderSpecialtyExceptionFilterHeader();
    }

    return (
      <>
        {this.renderSpecialtyExceptionFilterHeader()}
        <div className="row">
          {this.state.filteredIncludedSpecialtyGroupExceptions.map((includedSpecialtyGroupException) => {
            const included = includedSpecialtyGroupException.included;
            const specialtyException = includedSpecialtyGroupException.specialtyGroupException;
            const specialtyGroupExceptionId = specialtyException.specialtyGroupExceptionId;
            const templateSpecialtyGroup = this.state.sortedSpecialtyGroups ? this.state.sortedSpecialtyGroups.find((specialtyGroup) => specialtyGroup.code === specialtyException.specialtyGroupCode) : null;
            const specialtyGroupName = templateSpecialtyGroup ? templateSpecialtyGroup.name : "Not found in selected template";

            return (
              <div key={specialtyGroupExceptionId} className={`col-lg-4 col-md-4 col-sm-6 mb20 ${included ? "" : "text-muted"}`}>
                <QESCheckbox
                  containerClassName="pl5 pt2 btrl2 m-0 border-info bg-info ellipsis"
                  label={`${specialtyException.specialtyGroupCode} - ${specialtyGroupName}`}
                  className="mr5-imp"
                  checked={included}
                  onChange={(e) => this.onSpecialtyGroupExceptionIncludedChange(e.target.checked, specialtyGroupExceptionId)}
                  testId="clone-project__specialty-exception-checkbox"
                />
                <div className="pl10 pb10 brl2 border-info ellipsis text-warning">
                  {`${specialtyException.regionCode} - ${specialtyException.regionSetDescription}, ${specialtyException.regionSetStateName}`}
                </div>
                <div className="pl10 brl2 border-info pb-3">
                  <QESSelect
                    label="Status:"
                    disabled={!included}
                    value={specialtyException.specialtyGroupExceptionStatus}
                    id={`${specialtyGroupExceptionId}-status`}
                    className="mlr5"
                    onChange={(e) => this.onSpecialtyGroupExceptionStatusChange(e.target.value, specialtyGroupExceptionId)}
                    testId="clone-project__specialty-group-exception-status-checkbox"
                  >
                    <option value={SpecialtyGroupExceptionStatus.Pending} label="Pending">startCase(SpecialtyGroupExceptionStatus.Pending)</option>
                    <option value={SpecialtyGroupExceptionStatus.Submitted} label="Submitted">startCase(SpecialtyGroupExceptionStatus.Submitted)</option>
                    <option value={SpecialtyGroupExceptionStatus.Approved} label="Approved">startCase(SpecialtyGroupExceptionStatus.Approved)</option>
                    <option value={SpecialtyGroupExceptionStatus.Denied} label="Denied">startCase(SpecialtyGroupExceptionStatus.Denied)</option>
                    <option value={SpecialtyGroupExceptionStatus.Appealed} label="Appealed">startCase(SpecialtyGroupExceptionStatus.Appealed)</option>
                  </QESSelect>
                  <QESCheckbox
                    label="Previously Approved"
                    containerClassName="mb-0"
                    className="mr5-imp ml5-imp"
                    disabled={!included}
                    checked={specialtyException.previouslyApproved}
                    onChange={(e) => this.onSpecialtyGroupExceptionPreviouslyApprovedChange(e.target.checked, specialtyGroupExceptionId)}
                    testId="clone-project__specialty-group-exception-previously-approved-checkbox"
                  />
                </div>
                <div className="plr10 pb5 brbl2 border-info" >
                  <QESTextArea
                    label="Comments:"
                    disabled={!included}
                    value={specialtyException.comment}
                    className="form-control overflow-hidden"
                    rows={6}
                    placeholder="Comments"
                    onChange={(e) => this.onSpecialtyGroupExceptionCommentChange(e.target.value, specialtyGroupExceptionId)}
                    testId="clone-project__comments-textarea"
                    maxLength={4000}
                  />
                </div>
              </div>
            );
          })}
        </div>
        {this.props.enabledFeatureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS) &&
          <QESCheckbox
            label="Include Supplemental Documents"
            className="mr5-imp"
            checked={this.state.isIncludeSupplementaryDocumentsChecked}
            disabled={this.isIncludeSupplementalDocumentsDisabled()}
            onChange={this.handleIncludeSupplementaryDocumentsChange}
            testId="clone-project__include_supplemental_documents-checkbox"
          />
        }
      </>
    );
  }

  private isIncludeSupplementalDocumentsDisabled = () => {
    return this.state.includedSpecialtyGroupExceptions.length === 0 ||
      this.state.includedSpecialtyGroupExceptions.every(item => item.specialtyGroupException.supplementaryDocuments == null || item.specialtyGroupException.supplementaryDocuments.length == 0) ||
      this.state.includedSpecialtyGroupExceptions.every(sge => sge.included == false)
  }

  private handleIncludeSupplementaryDocumentsChange = () => {
    this.setState({
      isIncludeSupplementaryDocumentsChecked: !this.state.isIncludeSupplementaryDocumentsChecked
    })
  }

  private renderSpecialtyExceptionFilters = () => {
    const filterTypes: SpecialtyExceptionFilterType[] = [];
    for (const specialtyExceptionFilterType of this.filterValuesMap.keys()) {
      filterTypes.push(specialtyExceptionFilterType);
    }

    return (
      <div className="col-12 mb-2 mt-1">
        <div className="d-flex align-items-end">
          <QESSelect
            label="Filter by:"
            containerClassName="mr-2 col-3 pl-0"
            onChange={(e) => this.specialtyExceptionFilterTypeChange(e.target.value as SpecialtyExceptionFilterType)}
            value={this.state.selectedSpecialtyExceptionFilterType}
            testId="clone-project__exception-filter-fields-select"
          >
            {filterTypes.map((filterType) => {
              return (
                <option key={filterType} value={filterType} label={filterType}>{filterType}</option>
              );
            })}
          </QESSelect>
          {(this.state.specialtyFilterValues.length === 0 || null) &&
            <QESTextInput
              label="Filter Value"
              containerClassName="col-3"
              hideLabel={true}
              value={this.state.selectedSpecialtyExceptionFilterValue}
              onChange={(e) => this.specialtyExceptionFilterValueChange(e.target.value)}
              testId="clone-project__specialty-exception-filter-value-text-box"
            />
          }
          {(this.state.specialtyFilterValues.length > 0 || null) &&
            <QESSelect
              label="Filter Value"
              containerClassName="col-3"
              hideLabel={true}
              onChange={(e) => this.specialtyExceptionFilterValueChange(e.target.value)}
              value={this.state.selectedSpecialtyExceptionFilterValue}
              testId="clone-project__specialty-exception-filter-value"
            >
              {this.state.specialtyFilterValues.map((filterValue) => {
                return (
                  <option key={filterValue} value={filterValue} label={filterValue}>{filterValue}</option>
                );
              })}
            </QESSelect>
          }
          <QESButton
            qesType="secondary"
            className="ml-2"
            onClick={() => this.onFilterAddClick()}
            qesTestId="clone-project__add-exceptions-filter"
          >
            <QesIcon iconName={QesIcons.FontAwesome.Plus} isDecorative={true} width="14" /> Add Filter
          </QESButton>
        </div>
      </div>
    );
  }

  private renderSpecialtyExceptionFilterHeader = () => {
    const includedSpecialtyGroupCount = this.state.filteredIncludedSpecialtyGroupExceptions.filter((includedSpecialtyGroup) => includedSpecialtyGroup.included).length;
    return (
      <div className="row">
        <div className="col-12 mb-3">
          <div className="d-inline-flex align-items-center justify-content-between">
            <QESCheckbox
              label="Select All"
              containerClassName="d-inline-flex"
              onChange={(e) => this.checkAllSpecialtyGroupExceptionClick(e.target.checked)}
              indeterminate={(includedSpecialtyGroupCount > 0 && includedSpecialtyGroupCount !== this.state.filteredIncludedSpecialtyGroupExceptions.length)}
              checked={this.state.specialtyExceptionCheckAllChecked}
              testId="clone-project__check-all-specialty-group-exception-checkbox"
            />
            <span className="ml5 mr5">|</span>
            <QESButton qesType="secondary" className="p-0" onClick={() => this.onFilterIconClick()} qesTestId="clone-project__specialty-exception-filters" role="switch" aria-checked={this.state.showFilterBy}>
              <QesIcon iconName={this.state.showFilterBy ? QesIcons.FontAwesome.ToggleOn : QesIcons.FontAwesome.ToggleOff} isDecorative={true} width="12" /> Show filter fields
            </QESButton>
          </div>
          {(this.state.showFilterBy || null) && this.renderSpecialtyExceptionFilters()}
          {(this.state.selectedSpecialtyExceptionFilters.length > 0 || null) && this.renderSelectedSpecialtyExceptionFilters()}
        </div>
      </div >
    );
  }

  private renderSelectedSpecialtyExceptionFilters = () => {
    return (
      <div className="col-12 mt-3">
        <div className="overview-filters p-0 bg-transparent">
          {this.state.selectedSpecialtyExceptionFilters.map((filter) => {
            switch (filter.specialtyExceptionFilterType) {
              case SpecialtyExceptionFilterType.PreviouslyApproved:
                return (
                  <span
                    key={filter.specialtyExceptionFilterType}
                    onClick={() => this.onRemoveFilterClick(filter.specialtyExceptionFilterType)}
                    className="overview-filter"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {`Is ${filter.stringFilter === "False" ? "Not" : ""} Previously Approved`}<QesIcon iconName={QesIcons.FontAwesome.TimesCircle} description="remove this filter" width="12" classNames="ml-2 d-inline-block" />
                  </span>
                );
              case SpecialtyExceptionFilterType.Selected:
                return (
                  <span
                    key={filter.specialtyExceptionFilterType}
                    onClick={() => this.onRemoveFilterClick(filter.specialtyExceptionFilterType)}
                    className="overview-filter"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {`Is ${filter.stringFilter === "False" ? "Not" : ""} Selected`}<QesIcon iconName={QesIcons.FontAwesome.TimesCircle} description="remove this filter" width="12" classNames="ml-2 d-inline-block" />
                  </span>
                );
              case SpecialtyExceptionFilterType.RegionDescription:
                return (
                  <span
                    key={filter.specialtyExceptionFilterType}
                    onClick={() => this.onRemoveFilterClick(filter.specialtyExceptionFilterType)}
                    className="overview-filter"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {`County name contains '${filter.stringFilter}'`}<QesIcon iconName={QesIcons.FontAwesome.TimesCircle} description="remove this filter" width="12" classNames="ml-2 d-inline-block" />
                  </span>
                );
              case SpecialtyExceptionFilterType.SpecialtyCode:
                return (
                  <span
                    key={filter.specialtyExceptionFilterType}
                    onClick={() => this.onRemoveFilterClick(filter.specialtyExceptionFilterType)}
                    className="overview-filter"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {`Specialty code name contains '${filter.stringFilter}'`}<QesIcon iconName={QesIcons.FontAwesome.TimesCircle} description="remove this filter" width="12" classNames="ml-2 d-inline-block" />
                  </span>
                );
              case SpecialtyExceptionFilterType.StateName:
                return (
                  <span
                    key={filter.specialtyExceptionFilterType}
                    onClick={() => this.onRemoveFilterClick(filter.specialtyExceptionFilterType)}
                    className="overview-filter"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {`State name contains '${filter.stringFilter}'`}<QesIcon iconName={QesIcons.FontAwesome.TimesCircle} description="remove this filter" width="12" classNames="ml-2 d-inline-block" />
                  </span>
                );
              case SpecialtyExceptionFilterType.Status:
                return (
                  <span
                    key={filter.specialtyExceptionFilterType}
                    onClick={() => this.onRemoveFilterClick(filter.specialtyExceptionFilterType)}
                    className="overview-filter"
                    style={{ whiteSpace: "nowrap" }}
                  >
                    {`Status is '${filter.stringFilter}'`}<QesIcon iconName={QesIcons.FontAwesome.TimesCircle} description="remove this filter" width="12" classNames="ml-2 d-inline-block" />
                  </span>
                );
            }
          })}
        </div>
        <div className="pl10 mt-1">
          <small>
            {`${this.state.filteredIncludedSpecialtyGroupExceptions.length} of ${this.state.includedSpecialtyGroupExceptions.length} match the filter criteria`}
          </small>
        </div>
      </div>
    );
  }

  private renderShareUsersPanelContents = () => {
    if (this.state.resourceSharingUsers.length === 0) {
      return (
        <div className="row">
          <div className="col-sm-12 text-center text-muted">
            No available users found to share/unshare with
          </div>
        </div>
      );
    }
    const sharedCount = this.state.resourceSharingUsers.filter((user) => user.shared).length;
    return (
      <>
        <div className="row">
          <QESCheckbox
            label="Select All"
            containerClassName="col-md-12"
            onChange={(e) => this.checkAllShareUsersClick(e.target.checked)}
            indeterminate={(sharedCount > 0 && sharedCount !== this.state.resourceSharingUsers.length)}
            checked={this.state.shareUsersCheckAllChecked}
            testId="clone-project__share-users-check-all-checkbox"
          />
        </div>

        <div className="row">
          {this.state.resourceSharingUsers.map((user) => {
            return (
              <div key={user.userId} className="col-lg-3 col-md-4 col-sm-6 mb15">
                <div className={`pl5 pt2 btrl2 border-info bg-info ellipsis ${user.shared ? "" : "text-muted"}`}>
                  <QESCheckbox
                    label={user.fullName}
                    checked={user.shared && !user.readOnly}
                    disabled={user.readOnly}
                    onChange={(e) => this.onShareUserChange(e.target.checked, user.userId)}
                    testId={"clone-project__share-users-" + user.userId + "-checkbox"}
                  />
                </div>
                <div className={`pl10 pb10 brbl2 border-info ellipsis ${user.shared ? "text-warning" : "text-muted"}`}>
                  {user.userName}
                </div>
              </div>
            );
          })}
        </div>
      </>
    );
  }

  private renderNetNewProviderPanelContents = () => (
    <QESCheckbox
      containerClassName="col-12 form-check form-check-inline"
      label="Retain Net New Servicing Providers"
      checked={this.state.retainNetNewProviders}
      className="form-check-input"
      id="net-new"
      onChange={this.toggleNetNewProviders}
      testId="clone-project__retain-net-new-providers-checkbox"
    />
  )

  private onFilterIconClick = () => {
    this.setState((prevState) => ({ showFilterBy: !prevState.showFilterBy }));
  }

  private onRemoveFilterClick = (filterType: SpecialtyExceptionFilterType) => {
    this.setState((prevState) => {

      const newFilters: SpecialtyExceptionFilter[] = [];
      for (const filter of prevState.selectedSpecialtyExceptionFilters) {

        if (filter.specialtyExceptionFilterType !== filterType) {
          newFilters.push(filter);
        }
      }

      return { selectedSpecialtyExceptionFilters: newFilters };
    });
  }

  private specialtyExceptionFilterTypeChange = (filterType: SpecialtyExceptionFilterType) => {
    const specialtyFilterValues = this.filterValuesMap.get(filterType);
    let selectedSpecialtyExceptionFilterValue = "";

    if (specialtyFilterValues.length > 0) {
      selectedSpecialtyExceptionFilterValue = specialtyFilterValues[0];
    }

    this.setState({
      selectedSpecialtyExceptionFilterType: filterType,
      selectedSpecialtyExceptionFilterValue,
      specialtyFilterValues,
    });
  }

  private specialtyExceptionFilterValueChange = (filterValue: string) => {
    this.setState({ selectedSpecialtyExceptionFilterValue: filterValue });
  }

  private onFilterAddClick = () => {
    this.setState((prevState) => {

      const newFilters: SpecialtyExceptionFilter[] = [];
      for (const filter of prevState.selectedSpecialtyExceptionFilters) {

        if (filter.specialtyExceptionFilterType !== prevState.selectedSpecialtyExceptionFilterType) {
          newFilters.push(filter);
        }
      }

      newFilters.push(new SpecialtyExceptionFilter(prevState.selectedSpecialtyExceptionFilterType, prevState.selectedSpecialtyExceptionFilterValue));
      return { selectedSpecialtyExceptionFilters: newFilters };
    });
  }

  private toggleTermedProviders = (e: React.ChangeEvent) => {
    const check = (e.target as HTMLInputElement).checked;
    this.setState({
      disableTerminationCopy: !check,
    });
  }

  private toggleNetNewProviders = (e: React.ChangeEvent) => {
    const check = (e.target as HTMLInputElement).checked;
    this.setState({
      retainNetNewProviders: check,
    });
  }

  private checkAllRetainMarketClick = (retainMarketCheckAllChecked: boolean) => {
    this.setState({
      marketProjectionTypesToCopy: retainMarketCheckAllChecked ? [ProjectionProviderType.Target, ProjectionProviderType.ExcludeImpact] : [],
      retainMarketCheckAllChecked,
    });
  }

  private checkAllRetainProspectClick = (retainProspectCheckAllChecked: boolean) => {
    this.setState({
      prospectProjectionTypesToCopy: retainProspectCheckAllChecked ? [ProjectionProviderType.Target, ProjectionProviderType.ExcludeImpact] : [],
      retainProspectCheckAllChecked,
    });
  }

  private retainMarketClick = (checked: boolean, projectionProviderType: ProjectionProviderType) => {

    this.setState((prevState) => ({
      marketProjectionTypesToCopy: checked ?
        prevState.marketProjectionTypesToCopy.concat([projectionProviderType]) :
        prevState.marketProjectionTypesToCopy.filter((projectionType) => projectionType !== projectionProviderType),
      retainMarketCheckAllChecked: prevState.marketProjectionTypesToCopy.length === 1 && checked,
    }));
  }

  private retainProspectClick = (checked: boolean, projectionProviderType: ProjectionProviderType) => {

    this.setState((prevState) => ({
      prospectProjectionTypesToCopy: checked ?
        prevState.prospectProjectionTypesToCopy.concat([projectionProviderType]) :
        prevState.prospectProjectionTypesToCopy.filter((projectionType) => projectionType !== projectionProviderType),
      retainProspectCheckAllChecked: prevState.prospectProjectionTypesToCopy.length === 1 && checked,
    }));
  }

  private checkAllShareUsersClick = (checked: boolean) => {
    const resourceSharingUsers: ResourceSharingUserModel[] = [];

    for (const shareUser of this.state.resourceSharingUsers) {
      const updatedShareUser = cloneDeep(shareUser);
      if (!updatedShareUser.readOnly) {
        updatedShareUser.shared = checked;
      }
      resourceSharingUsers.push(updatedShareUser);
    }

    this.setState({
      resourceSharingUsers,
      shareUsersCheckAllChecked: checked,
    });
  }

  private onShareUserChange = (shared: boolean, userId: number) => {
    const resourceSharingUsers = [...this.state.resourceSharingUsers];
    const resourceIndex = resourceSharingUsers.findIndex((user) => user.userId === userId);

    resourceSharingUsers[resourceIndex] = cloneDeep(resourceSharingUsers[resourceIndex]);
    resourceSharingUsers[resourceIndex].shared = shared;

    let hasUnChecked = false;
    for (const shareUser of resourceSharingUsers) {
      hasUnChecked = hasUnChecked || !shareUser.shared;
    }

    this.setState({
      resourceSharingUsers,
      shareUsersCheckAllChecked: !hasUnChecked,
    });
  }

  private checkAllSpecialtyGroupExceptionClick = (checked: boolean) => {
    const includedSpecialtyGroupExceptions = [...this.state.includedSpecialtyGroupExceptions];

    //check all is at the context of what is visible to the user (using filter). It doesn't check/uncheck all exceptions
    for (const includedSpecialtyGroupException of this.state.filteredIncludedSpecialtyGroupExceptions) {
      const specialtyExceptionIndex =
        includedSpecialtyGroupExceptions.findIndex((includedException) =>
          includedException.specialtyGroupException.specialtyGroupExceptionId === includedSpecialtyGroupException.specialtyGroupException.specialtyGroupExceptionId);

      includedSpecialtyGroupExceptions[specialtyExceptionIndex] = cloneDeep(includedSpecialtyGroupExceptions[specialtyExceptionIndex]);
      includedSpecialtyGroupExceptions[specialtyExceptionIndex].included = checked;
    }

    let uncheckIncludeSupplementaryDocuments = false
    if (includedSpecialtyGroupExceptions.every(sge => sge.included == false)) {
      uncheckIncludeSupplementaryDocuments = true
    }

    this.setState((prevState) => ({
      ...prevState,
      includedSpecialtyGroupExceptions,
      isIncludeSupplementaryDocumentsChecked: uncheckIncludeSupplementaryDocuments ? false : prevState.isIncludeSupplementaryDocumentsChecked,
      specialtyExceptionCheckAllChecked: checked
    }));

  }

  private onSpecialtyGroupExceptionIncludedChange = (included: boolean, specialtyGroupExceptionId: number) => {
    const includedSpecialtyGroupExceptions = [...this.state.includedSpecialtyGroupExceptions];

    const specialtyExceptionIndex =
      includedSpecialtyGroupExceptions.findIndex((exception) =>
        exception.specialtyGroupException.specialtyGroupExceptionId === specialtyGroupExceptionId);

    includedSpecialtyGroupExceptions[specialtyExceptionIndex] = cloneDeep(includedSpecialtyGroupExceptions[specialtyExceptionIndex]);
    includedSpecialtyGroupExceptions[specialtyExceptionIndex].included = included;

    let uncheckIncludeSupplementaryDocuments = false
    if (includedSpecialtyGroupExceptions.every(sge => sge.included == false)) {
      uncheckIncludeSupplementaryDocuments = true
    }

    this.setState((prevState) => ({
      ...prevState,
      includedSpecialtyGroupExceptions,
      isIncludeSupplementaryDocumentsChecked: uncheckIncludeSupplementaryDocuments ? false : prevState.isIncludeSupplementaryDocumentsChecked
    }));

  }

  private onSpecialtyGroupExceptionStatusChange = (status: string, specialtyGroupExceptionId: number) => {
    const includedSpecialtyGroupExceptions = [...this.state.includedSpecialtyGroupExceptions];

    const specialtyExceptionIndex =
      includedSpecialtyGroupExceptions.findIndex((exception) =>
        exception.specialtyGroupException.specialtyGroupExceptionId === specialtyGroupExceptionId);
    includedSpecialtyGroupExceptions[specialtyExceptionIndex] = cloneDeep(includedSpecialtyGroupExceptions[specialtyExceptionIndex]);
    includedSpecialtyGroupExceptions[specialtyExceptionIndex].specialtyGroupException.specialtyGroupExceptionStatus = status as SpecialtyGroupExceptionStatus;

    this.setState({ includedSpecialtyGroupExceptions });
  }

  private onSpecialtyGroupExceptionPreviouslyApprovedChange = (previouslyApproved: boolean, specialtyGroupExceptionId: number) => {
    const includedSpecialtyGroupExceptions = [...this.state.includedSpecialtyGroupExceptions];

    const specialtyExceptionIndex =
      includedSpecialtyGroupExceptions.findIndex((exception) =>
        exception.specialtyGroupException.specialtyGroupExceptionId === specialtyGroupExceptionId);
    includedSpecialtyGroupExceptions[specialtyExceptionIndex] = cloneDeep(includedSpecialtyGroupExceptions[specialtyExceptionIndex]);
    includedSpecialtyGroupExceptions[specialtyExceptionIndex].specialtyGroupException.previouslyApproved = previouslyApproved;

    this.setState({ includedSpecialtyGroupExceptions });
  }

  private onSpecialtyGroupExceptionCommentChange = (comment: string, specialtyGroupExceptionId: number) => {
    const includedSpecialtyGroupExceptions = [...this.state.includedSpecialtyGroupExceptions];

    const specialtyExceptionIndex =
      includedSpecialtyGroupExceptions.findIndex((exception) =>
        exception.specialtyGroupException.specialtyGroupExceptionId === specialtyGroupExceptionId);

    includedSpecialtyGroupExceptions[specialtyExceptionIndex] = cloneDeep(includedSpecialtyGroupExceptions[specialtyExceptionIndex]);
    includedSpecialtyGroupExceptions[specialtyExceptionIndex].specialtyGroupException.comment = comment;

    this.setState({ includedSpecialtyGroupExceptions });
  }

  private handleTagggingChange = (tags: ResourceTagModel[]) => {
    const project: Project = { ...this.state.project, tags };
    this.setState({ project });
  }

  private handleNameChange = (projectName: string) => {
    const project: Project = { ...this.state.project };

    project.resource = {
      ...this.state.project.resource,
      name: projectName,
    };

    this.setState({ project });
  }

  private handleDescChange = (desc: string) => {
    const project: Project = { ...this.state.project };

    project.resource = {
      ...this.state.project.resource,
      description: desc,
    };

    this.setState({ project });
  }

  private handleIsPrivateChange = (isPrivate: boolean) => {
    const project: Project = { ...this.state.project };

    project.resource = {
      ...this.state.project.resource,
      isPrivate,
    };

    this.setState({ project });
  }

  private handleCheckBoxChange = (property: UpdateResourceKeys) => {
    const project: Project = {
      ...this.state.project,
      [property]: !this.state.project[property]
    };

    // if showPciData is being changed, turn off showPciDashboard, showLocationConfidenceScores, and showMotiveAppropriatenessScore, else keep current state
    if (property === "showPciData") {
      project.showLocationConfidenceScores = false;
      project.showMotiveAppropriatenessScore = false;
      project.showPciDashboard = false;
      project.showVaClaimsVolumeColumn = false;
      project.showTricareClaimsVolumeColumn = false;
      project.showCareJourneyScores = false;
    }

    // if showNextClosestNetworkProviders or hasZipSummaries is being changed, turn enableRegulatoryExceptionReports and showPatternOfCareProviders off if it was true
    if ((property === "showNextClosestNetworkProviders" || property === "hasZipSummaries") && (!!project.enableRegulatoryExceptionReports || !!project.showPatternOfCareProviders)) {
      project.enableRegulatoryExceptionReports = false;
      project.showPatternOfCareProviders = false;
    }

    this.setState({ project });
  }

  private handleRiskPopChanged = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const riskPop = +(e.target.value);
    const riskDescription = (e.target.options[e.target.selectedIndex].innerText);

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Risk) {
        x.memberSetId = riskPop;
      }
    });

    if (riskPop) {
      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          editProjectOverrideMemberSets: updates,
          membershipRiskMemberSetAlias: riskDescription,
          membershipRiskMemberSetId: riskPop,
        },
      }));
    } else {
      this.setState((prevState): CloneProjectState => ({
        ...prevState,
        project: {
          ...prevState.project,
          membershipRiskMemberSetAlias: "",
          membershipRiskMemberSetId: null,
        },
      }));
    }
  }

  private handleSpecialtyGroupsChange = (selectedRecords: TemplateSpecialtyGroup[]) => {
    const specialtyGroupCodes = this.state.sortedSpecialtyGroups.map((sg) => sg.code);
    const selectedSpecialtyGroupCodes = selectedRecords.map((sg) => sg.code);
    const excludedSpecialtyGroupCodes = difference(specialtyGroupCodes, selectedSpecialtyGroupCodes);

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        excludedSpecialtyGroupCodes,
      },
    }));
  }

  private handleTelehealthSpecialtyGroupChange = (selectedSpecialtyGroupCodes: string[]) => {
    this.setState((prevState) => {
      return (
        {
          ...prevState,
          project: {
            ...prevState.project,
            telehealthSpecialtyGroupCodes: selectedSpecialtyGroupCodes,
          },
        });
    },
    );
  }

  private handleNetworkExclusionsChange = (exclusion: ProjectExclusionType) => {
    switch (exclusion) {
      case ProjectExclusionType.DeactivatedFacility:
        this.setState({ excludeDeactivatedFacilityNetwork: !this.state.excludeDeactivatedFacilityNetwork });
        break;
      case ProjectExclusionType.DeactivatedProvider:
        this.setState({ excludeDeactivatedProviderNetwork: !this.state.excludeDeactivatedProviderNetwork });
        break;
      case ProjectExclusionType.InvalidNppesNpiFacility:
        this.setState({ excludeInvalidNppesNpiFacilityNetwork: !this.state.excludeInvalidNppesNpiFacilityNetwork });
        break;
      case ProjectExclusionType.InvalidNppesNpiProvider:
        this.setState({ excludeInvalidNppesNpiProviderNetwork: !this.state.excludeInvalidNppesNpiProviderNetwork });
        break;
      case ProjectExclusionType.MissingNpiFacility:
        this.setState({ excludeMissingNpiFacilityNetwork: !this.state.excludeMissingNpiFacilityNetwork });
        break;
      case ProjectExclusionType.MissingNpiProvider:
        this.setState({ excludeMissingNpiProviderNetwork: !this.state.excludeMissingNpiProviderNetwork });
        break;
      case ProjectExclusionType.MedicareOptOut:
        this.setState({ excludeMedicareOptOutNetwork: !this.state.excludeMedicareOptOutNetwork });
        break;
      case ProjectExclusionType.OIGExclusion:
        this.setState({ excludeOIGExclusionNetwork: !this.state.excludeOIGExclusionNetwork });
        break;
      case ProjectExclusionType.Precluded:
        this.setState({ excludePrecludedNetwork: !this.state.excludePrecludedNetwork });
        break;
    }
  }

  private handleImpactExclusionsChange = (exclusion: ProjectExclusionType) => {
    switch (exclusion) {
      case ProjectExclusionType.DeactivatedFacility:
        this.setState({ excludeDeactivatedFacilityImpact: !this.state.excludeDeactivatedFacilityImpact });
        break;
      case ProjectExclusionType.DeactivatedProvider:
        this.setState({ excludeDeactivatedProviderImpact: !this.state.excludeDeactivatedProviderImpact });
        break;
      case ProjectExclusionType.InvalidNppesNpiFacility:
        this.setState({ excludeInvalidNppesNpiFacilityImpact: !this.state.excludeInvalidNppesNpiFacilityImpact });
        break;
      case ProjectExclusionType.InvalidNppesNpiProvider:
        this.setState({ excludeInvalidNppesNpiProviderImpact: !this.state.excludeInvalidNppesNpiProviderImpact });
        break;
      case ProjectExclusionType.MissingNpiFacility:
        this.setState({ excludeMissingNpiFacilityImpact: !this.state.excludeMissingNpiFacilityImpact });
        break;
      case ProjectExclusionType.MissingNpiProvider:
        this.setState({ excludeMissingNpiProviderImpact: !this.state.excludeMissingNpiProviderImpact });
        break;
      case ProjectExclusionType.MedicareOptOut:
        this.setState({ excludeMedicareOptOutImpact: !this.state.excludeMedicareOptOutImpact });
        break;
      case ProjectExclusionType.OIGExclusion:
        this.setState({ excludeOIGExclusionImpact: !this.state.excludeOIGExclusionImpact });
        break;
      case ProjectExclusionType.Precluded:
        this.setState({ excludePrecludedImpact: !this.state.excludePrecludedImpact });
        break;
    }
  }

  private handleProvidersChanged = (value: string) => {
    if (value) {
      const networkProviderSetId = Number(value);

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          networkProviderSetId,
        },
      }));
    } else {
      this.setState((prevState): CloneProjectState => ({
        ...prevState,
        project: {
          ...prevState.project,
          networkProviderSetId: null,
        },
      }));
    }
  }

  private handleNetworkProviderAliasChanged = (newAlias: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        networkProviderSetAlias: newAlias,
      }
    }))
  }

  private handleProspectProviderAliasChanged = (newAlias: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        prospectProviderSetAlias: newAlias,
      }
    }))
  }

  private handleMarketProviderAliasChanged = (newAlias: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        marketProviderSetAlias: newAlias,
      }
    }))
  }

  private handleProspectsChanged = (value: string) => {
    if (value) {
      const prospectProviderSetId = Number(value);

      const disableProspectProjectionCopy = (!this.originalProspectProviderSetId || prospectProviderSetId !== this.originalProspectProviderSetId);

      this.setState((prevState) => ({
        ...prevState,
        disableProspectProjectionCopy,
        project: {
          ...prevState.project,
          prospectProviderSetId,
        },
        prospectProjectionTypesToCopy: disableProspectProjectionCopy ? [] : prevState.prospectProjectionTypesToCopy,
        retainProspectCheckAllChecked: disableProspectProjectionCopy ? false : prevState.retainProspectCheckAllChecked,
      }));
    } else {
      this.setState((prevState): CloneProjectState => ({
        ...prevState,
        disableProspectProjectionCopy: true,
        project: {
          ...prevState.project,
          prospectProviderSetId: null,
          useProspectProvidersForMarketAnalysis: false,
        },
        prospectProjectionTypesToCopy: [],
        retainProspectCheckAllChecked: false,
      }));
    }
  }

  private handleMarketChanged = (value: string) => {
    if (value) {
      const marketProviderSetId = Number(value);

      const disableMarketProjectionCopy = (!this.originalMarketProviderSetId || marketProviderSetId !== this.originalMarketProviderSetId);

      this.setState((prevState) => ({
        ...prevState,
        disableMarketProjectionCopy,
        marketProjectionTypesToCopy: disableMarketProjectionCopy ? [] : prevState.marketProjectionTypesToCopy,
        project: {
          ...prevState.project,
          marketProviderSetId,
          useMarketProvidersForImpact: !prevState.project.marketProviderSetId ? true : prevState.project.useMarketProvidersForImpact,
          useMarketProvidersForMarketAnalysis: !prevState.project.marketProviderSetId && !this.state.hideUseInMarketAnalysis ? true : prevState.project.useMarketProvidersForMarketAnalysis,
        },
        retainMarketCheckAllChecked: disableMarketProjectionCopy ? false : prevState.retainMarketCheckAllChecked,
      }));
    } else {
      this.setState((prevState): CloneProjectState => ({
        ...prevState,
        disableMarketProjectionCopy: true,
        marketProjectionTypesToCopy: [],
        project: {
          ...prevState.project,
          marketProviderSetId: null,
          useMarketProvidersForMarketAnalysis: false,
        },
        retainMarketCheckAllChecked: false,
      }));
    }
  }

  private handleNetworkTaxonomyChange = (networkTaxonomyData: NetworkTaxonomyData, isValid: boolean) => {
    this.setState({
      isNetworkTaxonomyValid: isValid,
      project: {
        ...this.state.project,
        networkTaxonomy: networkTaxonomyData,
      }
    })
  }

  private handleAccuracyTemplateCollectionChange = (templateCollectionId: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracyTemplateCollectionId: templateCollectionId,
        accuracyTemplateCorrelationId: "",
      },
    }));
  }

  private handleAccuracyTemplateCorrelationIdChange = async (templateCorrelationId: string) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracyTemplateCorrelationId: templateCorrelationId,
      },
    }));
  }

  private handleAccuracySpecialtyDefinitionIdChange = async (e: React.ChangeEvent<HTMLSelectElement>) => {
    const accuracySpecialtyDefinitionId = e.target.value;
    let sortedSpecialtyGroups: TemplateSpecialtyGroup[] = [];

    //reset specialty group codes accordion, then reload w/ new values
    this.setState((prevState): CloneProjectState => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracySpecialtyDefinitionId,
        //if accuracy specialty is set, reset adequacy
        adequacyTemplateCollectionId: null,
        adequacyTemplateCorrelationId: "",
        excludedSpecialtyGroupCodes: [],
        memberSetId: null,
        telehealthSpecialtyGroupCodes: [],
      },
      projectAccessCodeMapSets: [],
      projectMemberPopulations: [],

      sortedSpecialtyGroups: [],
    }));

    sortedSpecialtyGroups = await this.getAccuracySpecialtyGroups(accuracySpecialtyDefinitionId);

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
      },
      sortedSpecialtyGroups,
    }));
  }

  private handleAccuracySpecialtyToQdpCodeMapChange = (value: number) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accuracySpecialtyToQdpCodeMapSetId: value || null,
      },
    }));
  }

  private handleNetworkSpecialtyCodeMapChange = (value: number) => {
    const prevState = this.state;

    this.setState({
      project: {
        ...prevState.project,
        networkSpecialtyCodeMapSetId: value || null,
      },
    });
  }

  private handleMarketSpecialtyCodeMapChange = (value: number) => {
    const prevState = this.state;

    this.setState({
      project: {
        ...prevState.project,
        marketSpecialtyCodeMapSetId: value || null,
      },
    });
  }

  private handleUseAccuracyChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    let sortedAuthorizedInsurances: ClientInsuranceModel[] = [...this.state.authorizedInsurances];
    let error = this.state.error;

    if (checked && sortedAuthorizedInsurances.length === 0) {
      try {
        this.setState((prevState) => ({ ...prevState, accuracyInsurancesIsLoading: true, useAccuracy: checked }));
        const authorizedInsurances = await this.insuranceService.getAuthorizedInsurancesByClientId();
        sortedAuthorizedInsurances = authorizedInsurances && sortBy(authorizedInsurances, "planName");
      }
      catch {
        error = "Unable to load available accuracy insurance plans at this time.";
      }
    }

    this.setState((prevState): CloneProjectState => ({
      ...prevState,
      accuracyInsurancesIsLoading: false,
      authorizedInsurances: sortedAuthorizedInsurances,
      error: error,
      project:
      {
        ...prevState.project,
        accuracySpecialtyDefinitionId: null,
        accuracyTemplateCollectionId: null,
        accuracyTemplateCorrelationId: null,
      },
      useAccuracy: checked,
    }));
  }

  private handleUseAdequacyChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const checked = !!e.target.checked;
    //reset adequacy fields when check box changes
    this.setState((prevState): CloneProjectState => ({
      ...prevState,
      isZipProject: false,
      project:
      {
        ...prevState.project,
        accessCodeMapSetId: null,
        accuracySpecialtyDefinitionId: null,
        adequacyTemplateCollectionId: null,
        adequacyTemplateCorrelationId: null,
        displayOnNetworkAccordion: !checked ? null : prevState.displayOnNetworkAccordion,
        excludedSpecialtyGroupCodes: [],
        marketProviderSetId: null,
        marketSpecialtyCodeMapSetId: null,
        memberSetAlias: null,
        memberSetAlias2: null,
        memberSetId: null,
        memberSetId2: null,
        networkProviderSetId: null,
        networkSpecialtyCodeMapSetId: null,
        prospectProviderSetId: null,
        showAffiliationsInsights: !checked ? null : prevState.project.showAffiliationsInsights,
        showCareJourneyScores: !checked ? null : prevState.project.showCareJourneyScores,
        showLocationConfidenceScores: !checked ? null : prevState.project.showLocationConfidenceScores,
        showMotiveAppropriatenessScore: !checked ? null : prevState.project.showMotiveAppropriatenessScore,
        showPciDashboard: !checked ? null : prevState.project.showPciDashboard,
        showPciData: !checked ? null : prevState.project.showPciData,
        showTricareClaimsVolumeColumn: !checked ? null : prevState.project.showTricareClaimsVolumeColumn,
        showVaClaimsVolumeColumn: !checked ? null : prevState.project.showVaClaimsVolumeColumn,
        telehealthSpecialtyGroupCodes: [],
        useMarketProvidersForImpact: null,
      },
      projectAccessCodeMapSets: [],
      projectMemberPopulations: [],
      specialtyCodeMapSets: [],
      //selectedSpecialtyGroupCodes: [],
      useAdequacy: checked,
    }));
    this.freshOverrides();
    this.removeAlternateMemberships();
    this.generateSpecialtyOverrides(this.state.specialtyGroups);
  }

  private handleAuthorizedInsurancesChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    //set state for currently selected insurance
    const uid = e.target.value;

    this.setState((prevState) => ({
      ...prevState,
      selectedInsuranceUid: uid,
    }));
  }

  private handleAddInspectedInsurance = () => {
    const uid = this.state.selectedInsuranceUid;
    const selectedInsurances = this.state.selectedInsurances;

    if (!uid) { return; }

    //get the insurance we want to add
    const insToAdd = this.state.authorizedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    //check if insurance we want to add is already in list of selected insurances
    const existingIns = selectedInsurances.find((ins) => {
      return ins.uid === uid;
    });

    if (insToAdd && !existingIns) {
      selectedInsurances.push(insToAdd);

      const sortedSelectedInsurances = selectedInsurances && sortBy(selectedInsurances, "planName");

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          inspectedInsurancesList: sortedSelectedInsurances,
        },
        selectedInsurances: sortedSelectedInsurances,
      }));
    }
  }

  private handleRemoveInspectedInsurance = (uid: string) => {
    const selectedInsurances = this.state.selectedInsurances;
    const index = selectedInsurances.findIndex((ins) => ins.uid === uid);

    if (uid && index > -1) {
      selectedInsurances.splice(index, 1);

      this.setState((prevState) => ({
        ...prevState,
        project: {
          ...prevState.project,
          inspectedInsurancesList: selectedInsurances,
        },
        selectedInsurances,
      }));
    }
  }

  private removeAlternateMemberships = () => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = [];
    (project.editProjectOverrideMemberSets ?? []).forEach((x) => {
      if (x.projectOverrideMemberSetType !== ProjectOverrideMemberSetType.Alternate) {
        updates.push(x);
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
      },
    }));
  }

  private freshOverrides = () => {
    const primary: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: this.state.project.projectId,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Primary,
      secondarySpecialtyGroupCodes: [],
    };
    const secondary: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: this.state.project.projectId,
      memberSetId: null,
      memberSetName: null,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Secondary,
      secondarySpecialtyGroupCodes: [],
    };
    const risk: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: this.state.project.projectId,
      memberSetId: null,
      memberSetName: "Risk Analysis Membership",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Risk,
      secondarySpecialtyGroupCodes: [],
    };
    const editProjectOverrideMemberSets: EditProjectOverrideMemberSet[] = [];
    editProjectOverrideMemberSets.push(primary);
    editProjectOverrideMemberSets.push(secondary);
    if (this.props.user.roles.some((s) => s === Roles.MEMBERSHIP_RISK_USER)) { editProjectOverrideMemberSets.push(risk); }

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets,
      },
    }));
  }

  private handleTemplateCollectionChange = (templateCollectionId: number) => {
    const { project, templateCollections } = this.state;

    const prevTemplateCollectionResourceType = templateCollections.find(collection => collection.templateCollectionId == project.adequacyTemplateCollectionId)?.resource.resourceType;
    const currTemplateCollectionResourceType = templateCollections.find(collection => collection.templateCollectionId == templateCollectionId)?.resource.resourceType;
    const shouldShowZipCloneWarning = (prevTemplateCollectionResourceType != currTemplateCollectionResourceType) && (currTemplateCollectionResourceType == ResourceType.AdequacyZipTemplateCollection);

    const isZipTemplate = this.state.templateCollections.find((tc) => tc.templateCollectionId === templateCollectionId)?.resource.resourceType === ResourceType.AdequacyZipTemplateCollection;
    const thingsToReset: Partial<CloneProjectState> = cloneThingsToResetIfZipProject(isZipTemplate) as Partial<CloneProjectState>;
    const projectThingsToReset: Partial<Project> = cloneProjectThingsToResetIfZipProject(isZipTemplate);

    const updateTemplateState = () => {
      this.setState((prevState): CloneProjectState => ({
        ...prevState,
        isZipProject: isZipTemplate,
        onCloneToZipProjectWarningSubmit: null,
        project: {
          ...prevState.project,
          accessCodeMapSetId: null,
          accuracySpecialtyDefinitionId: null,
          adequacyTemplateCollectionId: templateCollectionId,
          adequacyTemplateCorrelationId: "",
          excludeInVolumeActions: isZipTemplate ? true : prevState.project.excludeInVolumeActions,
          excludedSpecialtyGroupCodes: [],
          memberSetId: null,
          strictTimeAndDistance: isZipTemplate ? true : prevState.project.strictTimeAndDistance,
          telehealthSpecialtyGroupCodes: [],
          timeDistanceCalculatorType: null,
          ...projectThingsToReset,
        },
        projectAccessCodeMapSets: [],
        projectMemberPopulations: [],
        showCloneToZipProjectWarning: false,
        sortedSpecialtyGroups: null,
        timeDistanceCalculatorTypes: [],
        ...thingsToReset,
      }));
    };

    if (shouldShowZipCloneWarning) {
      this.setState({
        onCloneToZipProjectWarningSubmit: updateTemplateState,
        showCloneToZipProjectWarning: true,
      });
    } else {
      updateTemplateState();
    }
  }

  private handleTemplateCorrelationIdChange = async (templateCorrelationId: string, userRoles: string[]) => {
    if (templateCorrelationId === "") {
      this.setState((prevState): CloneProjectState => ({
        ...prevState,
        project: {
          ...prevState.project,
          adequacyTemplateCorrelationId: templateCorrelationId,
          excludedSpecialtyGroupCodes: [],
          telehealthSpecialtyGroupCodes: [],
        },
        projectAccessCodeMapSets: [],
        projectMemberPopulations: [],
        sortedSpecialtyGroups: null,
      }));
    } else {
      const templateInfo =
        await this.getTemplateInfo(
          this.state.allAccessCodeMapSets,
          this.state.allMemberPopulations,
          this.state.templateCollections,
          this.state.project.adequacyTemplateCollectionId,
          templateCorrelationId);

      const sortedSpecialtyGroups = templateInfo.specialtyGroups && sortBy(templateInfo.specialtyGroups, this.sortOrder);

      const roleCalculatorTypeMap: { [key: string]: TimeDistanceCalculatorType } = {
        [Roles.GEOGRAPHY_AWARE_DISTANCE_USER]: TimeDistanceCalculatorType.GeographyAware,
        [Roles.ROUTING_DISTANCE_USER]: TimeDistanceCalculatorType.GraphhopperRouting,
        [Roles.STRAIGHT_LINE_DISTANCE_USER]: TimeDistanceCalculatorType.StraightLine,
      };

      const filteredTypes = templateInfo.timeDistanceCalculatorTypes.filter(t =>
        t === TimeDistanceCalculatorType.Quest || userRoles.find(r => roleCalculatorTypeMap[r] === t));

      const hasCmsApplicationCredits: boolean = sortedSpecialtyGroups.some(specialtyGroup => specialtyGroup.hasCMSApplicationCreditPercentages);

      const showMotiveAppropriatenessScore = templateInfo.motiveCrosswalkCodeMapSetId !== undefined && templateInfo.motiveCrosswalkCodeMapSetId !== null;


      this.setState((prevState): CloneProjectState => ({
        ...prevState,
        hideCMSApplicationCredit: !hasCmsApplicationCredits,
        project: {
          ...prevState.project,
          adequacyTemplateCorrelationId: templateCorrelationId,
          excludedSpecialtyGroupCodes: [],
          showCMSCredit: !hasCmsApplicationCredits ? false : this.state.project.showCMSCredit,
          showMotiveAppropriatenessScore: !showMotiveAppropriatenessScore ? false : this.state.project.showMotiveAppropriatenessScore,
          telehealthSpecialtyGroupCodes: [],
          timeDistanceCalculatorType:
            (filteredTypes && filteredTypes.length > 0)
              ? prevState.project.timeDistanceCalculatorType
                ? filteredTypes.find(t => t === prevState.project.timeDistanceCalculatorType)
                  ? filteredTypes.find(t => t === prevState.project.timeDistanceCalculatorType)
                  : filteredTypes[0]
                : filteredTypes[0]
              : null,
        },
        projectAccessCodeMapSets: templateInfo.projectAccessCodeMapSets,
        projectMemberPopulations: templateInfo.projectMemberPopulations,
        sortedSpecialtyGroups,
        templateAccessGranularity: templateInfo.templateAccessGranularity,
        timeDistanceCalculatorTypes: templateInfo.timeDistanceCalculatorTypes,
      }));
    }
  }

  private handleTimeDistanceCalculatorTypeChange = (timeDistanceCalculatorType: TimeDistanceCalculatorType) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        timeDistanceCalculatorType,
      },
    }));
  }

  private async getTemplateInfo(
    allAccessCodeMapSets: CodeMapSet[],
    allMemberPopulations: MemberSetModel[],
    templateCollections: TemplateCollectionMetadata[],
    templateCollectionId: number,
    templateCorrelationId: string) {
    // Setup the retrieval of the specialty groups while the member sets and code map sets are fetched.

    let projectAccessCodeMapSets: CodeMapSet[] = allAccessCodeMapSets;
    let projectMemberPopulations: MemberSetModel[] = allMemberPopulations;
    let motiveCrosswalkCodeMapSetId: number = null;

    // If the first selection in the dropdown list of correlation IDs is chosen, we need to clear the access codeMap options
    if (templateCorrelationId === "") {
      projectAccessCodeMapSets = [];
      projectMemberPopulations = [];
    } else {
      // Get to the template, using the chosen collection, and the correlation ID
      for (let collection of templateCollections) {
        if (collection.templateCollectionId === templateCollectionId) {
          // Populate the collection with template information
          collection = await this.templateService.getTemplateCollectionById(collection.templateCollectionId);

          // Found the template collection
          for (const template of collection.templates) {
            if (template.correlationId === templateCorrelationId) {
              // Found the template - now filter down the access codeMaps available for selection to those that are compatible with the chosen template
              if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.ZipClass) {
                projectAccessCodeMapSets = projectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.ZipCodeClassMap);
              } else if (template.accessCriteriaSpecificationGranularity === AccessCriteriaGranularityEnum.CountyClass) {
                projectAccessCodeMapSets = projectAccessCodeMapSets.filter((r) => r.codeMapType === CodeMapType.CountyClassMap);
              } else {
                projectAccessCodeMapSets = [];
              }

              // Suggest the codemapset and memberset that are specified by the template, by moving them to the top of their respective list
              if (projectAccessCodeMapSets && projectAccessCodeMapSets.length > 0 && template.accessCodeMapSetId !== null && template.accessCodeMapSetId !== undefined) {
                projectAccessCodeMapSets.sort((x, y) => x.codeMapSetId === template.accessCodeMapSetId ? -1 : y.codeMapSetId === template.accessCodeMapSetId ? 1 : 0);
              }
              if (projectMemberPopulations && projectMemberPopulations.length > 0 && template.memberSetId !== null && template.memberSetId !== undefined) {
                projectMemberPopulations.sort((x, y) => x.memberSetId === template.memberSetId ? -1 : y.memberSetId === template.memberSetId ? 1 : 0);
              }

              motiveCrosswalkCodeMapSetId = template.motiveCrosswalkCodeMapSetId === undefined ? null : template.motiveCrosswalkCodeMapSetId;
            }
          }
        }
      }
    }

    //the clone will always run the latest version of the template. No need to get the old project's exact template.
    const specialtyGroups = await this.templateService.getLatestTemplateSpecialtyGroupsByCorrelationId(templateCollectionId, templateCorrelationId);
    const templateMetadata =
      await this.templateService.getLatestTemplateByCorrelationId(templateCollectionId, templateCorrelationId, true);

    return {
      attestationCrosswalkCodeMapSetId: templateMetadata.attestationCrosswalkCodeMapSetId,
      motiveCrosswalkCodeMapSetId,
      projectAccessCodeMapSets,
      projectMemberPopulations,
      specialtyGroups,
      templateAccessGranularity: templateMetadata.accessCriteriaSpecificationGranularity,
      timeDistanceCalculatorTypes: templateMetadata.timeDistanceCalculatorTypes,
    };
  }

  private generateAltMemberships = () => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    const { projectMemberPopulations } = this.state;
    let altMembers: MemberSetModel[] = [];
    let empty = true;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate && x.memberSetId != null) {
        empty = false;
        if (!!projectMemberPopulations) {
          projectMemberPopulations.forEach((y) => {
            if (x.memberSetId === y.memberSetId) {
              altMembers.push(y);
            }
          });
        }
      }
    });
    if (empty === true) {
      altMembers = this.state.projectMemberPopulations;
    }
    this.setState((prevState) => ({
      ...prevState,
      alternateMemberships: altMembers,
    }));
  }

  private toggleShowClosestProviderReportOption = () => {
    const { project } = this.state;
    const currentMemberPops = this.state.allMemberPopulations.filter(function (pop) {
      return (pop.memberSetId == project.memberSetId || pop.memberSetId == project.memberSetId2 ||
        (project.editProjectOverrideMemberSets ?? []).some(function (overrideMemberSet) { return pop.memberSetId == overrideMemberSet.memberSetId }))
    })

    const anyCensus = currentMemberPops.some(function (pop) {
      return pop.memberSetType == MemberSetTypeEnum.Census;
    })
    let hideClosestProviderReportOption = false;
    let showClosestProviderReport = this.initialShowClosestProviderSetting;
    if (anyCensus) {
      hideClosestProviderReportOption = true;
      showClosestProviderReport = false;
    }

    this.setState((prevState) => ({
      ...prevState,
      hideClosestProviderReportOption: hideClosestProviderReportOption,
      project: {
        ...prevState.project,
        showClosestProviderReport: showClosestProviderReport,
      },
    }));
  }

  private handleMemberPopChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Primary) {
        x.memberSetId = memberPop;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
        memberSetId: memberPop ? memberPop : null,
      },
    }), this.toggleShowClosestProviderReportOption);
  }

  private handleMemberPopChange2 = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const memberPop = +(e.target.value);

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Secondary) {
        x.memberSetId = memberPop;
      }
    });

    let alias = "";
    if (memberPop) {
      alias = project.memberSetAlias2;
    }

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
        memberSetAlias2: alias,
        memberSetId2: memberPop ? memberPop : null,
      },
    }), this.toggleShowClosestProviderReportOption);
  }

  private handleMemberPopAliasChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = project.editProjectOverrideMemberSets;
    const memberSetAlias = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === "Primary") {
        x.alias = e.target.value;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        memberSetAlias,
      },
    }));
  }

  private handleMemberPopAliasChange2 = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    const memberSetAlias2 = e.target.value;
    updates.forEach((x) => {
      if (x.projectOverrideMemberSetType === "Secondary") {
        x.alias = e.target.value;
      }
    });

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
        memberSetAlias2,
      },
    }));
  }

  private handleNewAlternativeMemberPop = () => {
    const newAltMember: EditProjectOverrideMemberSet = {
      alias: "",
      customProjectId: null,
      memberSetId: null,
      memberSetName: "",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Alternate,
      secondarySpecialtyGroupCodes: [],
    };

    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    updates.push(newAltMember);
    this.toggleMemberBinding();

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        createProjectOverrideMemberSets: updates,
      },
    }));
  }

  private toggleMemberBinding = () => {
    let tracker = false;
    const { project } = this.state;
    const overrides: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    overrides.forEach((x) => {
      if (x.projectOverrideMemberSetType === "Alternate" && x.memberSetId != null) {
        this.setState((prevState) => ({ ...prevState, disableMemberBindingTable: false }));
        tracker = true;
      }
    });
    if (tracker === false) {
      this.setState((prevState) => ({ ...prevState, disableMemberBindingTable: true }));
    }
  }

  private populateOverride = () => {
    const primary: EditProjectOverrideMemberSet = {
      alias: this.state.project.memberSetAlias,
      customProjectId: this.state.project.projectId,
      memberSetId: this.state.project.memberSetId,
      memberSetName: this.state.project.memberSetName,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Primary,
      secondarySpecialtyGroupCodes: [],
    };
    const secondary: EditProjectOverrideMemberSet = {
      alias: this.state.project.memberSetAlias2,
      customProjectId: this.state.project.projectId,
      memberSetId: this.state.project.memberSetId2,
      memberSetName: this.state.project.memberSetName2,
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Secondary,
      secondarySpecialtyGroupCodes: [],
    };
    const risk: EditProjectOverrideMemberSet = {
      alias: this.state.project.membershipRiskMemberSetAlias,
      customProjectId: this.state.project.projectId,
      memberSetId: this.state.project.membershipRiskMemberSetId,
      memberSetName: "Risk Analysis Membership",
      primarySpecialtyGroupCodes: [],
      projectOverrideMemberSetId: null,
      projectOverrideMemberSetType: ProjectOverrideMemberSetType.Risk,
      secondarySpecialtyGroupCodes: [],
    };
    const editProjectOverrideMemberSets: EditProjectOverrideMemberSet[] = (this.state.project.editProjectOverrideMemberSets ?? []);
    if (this.props.user.roles.some((r) => r === Roles.MEMBERSHIP_RISK_USER)) { editProjectOverrideMemberSets.unshift(risk); }
    editProjectOverrideMemberSets.unshift(secondary);
    //editProjectOverrideMemberSets.unshift(secondary);
    editProjectOverrideMemberSets.unshift(primary);

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets,
      },
    }));
  }

  private handleAlternateMemberPopAliasChange = (rows: EditProjectOverrideMemberSet[]) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: rows,
      },
    }));
  }

  private handleAlternateMemberPopChange = (memberPopId: number, prevMemberPopId: number) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    const memberName = this.state.allMemberPopulations.filter((ind) => ind.memberSetId === memberPopId)[0]?.resource.name;
    if (prevMemberPopId === null) {
      for (const x of updates) {
        if (x.memberSetId === null && x.projectOverrideMemberSetType === ProjectOverrideMemberSetType.Alternate) {
          x.memberSetId = memberPopId;
          x.memberSetName = memberName;
          break;
        }
      }
    } else {
      updates.forEach((x) => {
        if (x.memberSetId === prevMemberPopId) {
          x.memberSetId = memberPopId;
          x.memberSetName = memberName;
          x.primarySpecialtyGroupCodes.forEach((y) => {
            this.setPrimarySpecialtyOverride(memberPopId, y);
          });
          x.secondarySpecialtyGroupCodes.forEach((y) => {
            this.setSecondarySpecialtyOverride(memberPopId, y);
          });
        }
      });
    }

    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        createProjectOverrideMemberSets: updates,
      },
    }), this.toggleShowClosestProviderReportOption);

    this.toggleMemberBinding();
  }

  private setPrimarySpecialtyOverride = (memberPop: number, code: string) => {
    const { specialtyOverrides } = this.state;
    specialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.primaryMembership = memberPop;
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private setSecondarySpecialtyOverride = (memberPop: number, code: string) => {
    const { specialtyOverrides } = this.state;
    specialtyOverrides.forEach((x) => {
      if (x.code === code) {
        x.secondaryMembership = memberPop;
      }
    });
    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private handleDeleteAlternateRow = (rows: EditProjectOverrideMemberSet[]) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: rows,
      },
    }), this.toggleShowClosestProviderReportOption);
    this.generateAltMemberships();
    this.toggleMemberBinding();
  }

  private handlePrimaryMembershipBindingChange = (memberPop: number, code: string) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    if (memberPop === null) {
      updates.forEach((x) => {
        x.primarySpecialtyGroupCodes.forEach((y) => {
          if (y === code) {
            x.primarySpecialtyGroupCodes.splice(x.primarySpecialtyGroupCodes.indexOf(y), 1);
          }
        });
      });
    } else {
      updates.forEach((x) => {
        if (x.memberSetId === memberPop) {
          x.primarySpecialtyGroupCodes.push(code);
        }
      });
    }
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
      },
    }));
  }

  private handleSecondaryMembershipBindingChange = (memberPop: number, code: string) => {
    const { project } = this.state;
    const updates: EditProjectOverrideMemberSet[] = (project.editProjectOverrideMemberSets ?? []);
    if (memberPop === null) {
      updates.forEach((x) => {
        x.secondarySpecialtyGroupCodes.forEach((y) => {
          if (y === code) {
            x.secondarySpecialtyGroupCodes.splice(x.secondarySpecialtyGroupCodes.indexOf(y), 1);
          }
        });
      });
    } else {
      updates.forEach((x) => {
        if (x.memberSetId === memberPop) {
          x.secondarySpecialtyGroupCodes.push(code);
        }
      });
    }
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        editProjectOverrideMemberSets: updates,
      },
    }));
  }

  private generateSpecialtyOverrides = (sortedRecords: TemplateSpecialtyGroup[]) => {
    const specialtyOverrides: SpecialtyOverrides[] = [];

    if (sortedRecords) {
      sortedRecords.forEach((x) => {
        specialtyOverrides.push({ code: x.code, primaryMembership: null, secondaryMembership: null });
      });

      specialtyOverrides.forEach((x) => {
        (this.state.project.editProjectOverrideMemberSets ?? []).forEach((y) => {
          y.primarySpecialtyGroupCodes.forEach((z) => {
            if (z === x.code) {
              x.primaryMembership = y.memberSetId;
            }
          });
          y.secondarySpecialtyGroupCodes.forEach((z) => {
            if (z === x.code) {
              x.secondaryMembership = y.memberSetId;
            }
          });
        });
      });
    }

    this.setState((prevState) => ({
      ...prevState,
      specialtyOverrides,
    }));
  }

  private handleAccessCodeMapSetChange = (accessCodeMapSetId: number) => {
    this.setState((prevState) => ({
      ...prevState,
      project: {
        ...prevState.project,
        accessCodeMapSetId: accessCodeMapSetId ? accessCodeMapSetId : null,
      },
    }));
  }

  private isCloneDisabled = () => {
    return this.state.loading ||
      this.state.cloneRunning ||
      !(this.state.project.resource.name !== null &&
        this.state.project.resource.name !== "" &&
        (!!this.state.projectNames && !this.state.projectNames.includes(this.state.project.resource.name))) ||
      this.state.project.resource.name === "" ||
      this.state.project.networkProviderSetId === null ||
      this.state.project.networkProviderSetId === 0 ||
      (this.state.sortedSpecialtyGroups &&
        this.state.sortedSpecialtyGroups.length ===
        this.state.project.excludedSpecialtyGroupCodes.length) ||
      this.state.project.resource.description === "" ||
      !this.state.project.timeDistanceCalculatorType || this.state.project.timeDistanceCalculatorType === undefined ||
      !this.state.isNetworkTaxonomyValid ||
      this.state.project?.tags?.some(tag => tag.tagGroupName.length > 0 && tag.tagName.length === 0) ||
      this.state.project?.tags?.some(tag => tag.tagGroupName.length === 0 && tag.tagName.length > 0);
  }

  private handleCloneRun = async (runNow: boolean) => {
    try {
      const cloneProjectModel = await this.getCloneProject();
      const correspondingProjectService = this.getProjectServiceOrZipProjectServiceByCollectionId(cloneProjectModel.adequacyTemplateCollectionId);

      await correspondingProjectService.cloneProject(cloneProjectModel, runNow);

      window.location.href = `${window.customUIBaseUrl}/`;
    } catch (error) {
      console.error(error);
      this.setState({ error: CloneProjectErrorTemplates.Unexpected });
    }
  }

  /**
   * Decides whether to clone using ProjectService.ts or ZipProjectService.ts depending on the target AdequacyTemplateCollection's ResourceType.
   * @param templateCollectionId
   * @returns The correct service for cloning to the desired project type
   */
  private getProjectServiceOrZipProjectServiceByCollectionId = (templateCollectionId: number) => {
    if (templateCollectionId == null) {
      // Accuracy projects don't have an adequacy template.
      return this.projectService;
    }

    const { templateCollections } = this.state;
    const templateCollectionResourceType = templateCollections.find(collection => collection.templateCollectionId == templateCollectionId)?.resource.resourceType;

    switch (templateCollectionResourceType) {
      case ResourceType.AdequacyZipTemplateCollection:
        return this.zipProjectService;
      case ResourceType.AdequacyTemplateCollection:
        return this.projectService;
      default: // We will just return the original endpoint, just in case
        return this.projectService;
    }
  }

  private getCloneProject = async () => {
    this.setState({ cloneRunning: true });
    await this.exclusionsToSingleArray();

    const createSpecialtyGroupExceptions: CreateSpecialtyGroupException[] = [];

    if (!this.state.isZipProject && this.state.includedSpecialtyGroupExceptions.some((exception) => exception.included)) {
      for (const includedSpecialtyGroupException of this.state.includedSpecialtyGroupExceptions) {
        if (includedSpecialtyGroupException.included) {
          createSpecialtyGroupExceptions.push({
            comment: includedSpecialtyGroupException.specialtyGroupException.comment,
            dateStatusUpdated: new Date(),
            dateUpdated: new Date(),
            endDate: includedSpecialtyGroupException.specialtyGroupException.endDate,
            exceptionNarrative: includedSpecialtyGroupException.specialtyGroupException.exceptionNarrative,
            previouslyApproved: includedSpecialtyGroupException.specialtyGroupException.previouslyApproved,
            regionSetId: includedSpecialtyGroupException.specialtyGroupException.regionSetId,
            specialtyGroupCode: includedSpecialtyGroupException.specialtyGroupException.specialtyGroupCode,
            specialtyGroupExceptionStatus: includedSpecialtyGroupException.specialtyGroupException.specialtyGroupExceptionStatus,
            startDate: includedSpecialtyGroupException.specialtyGroupException.startDate,
            supplementaryDocuments: this.state.isIncludeSupplementaryDocumentsChecked &&
              includedSpecialtyGroupException.specialtyGroupException?.supplementaryDocuments?.length > 0 &&
              this.props.enabledFeatureFlags.has(Features.VOLUME_EXCEPTIONS_SUPPLEMENTARY_DOCUMENTS) ?
              includedSpecialtyGroupException.specialtyGroupException.supplementaryDocuments :
              null
          } as CreateSpecialtyGroupException);
        }
      }
    }

    const copyProjectionProviders: CopyProjectionProvider[] = [];
    if (this.state.marketProjectionTypesToCopy.length > 0) {
      copyProjectionProviders.push({ projectionProviderTypes: this.state.marketProjectionTypesToCopy, providerNetworkType: ProviderNetworkType.Market } as CopyProjectionProvider);
    }

    if (this.state.prospectProjectionTypesToCopy.length > 0) {
      copyProjectionProviders.push({ projectionProviderTypes: this.state.prospectProjectionTypesToCopy, providerNetworkType: ProviderNetworkType.Prospect } as CopyProjectionProvider);
    }

    const shareUserIds = this.state.resourceSharingUsers.filter((user) => user.shared).map(
      (user) => user.userId);

    const sourceCustomProjectId = Number(this.props.match.params.projectId);

    const cloneProjectModel = {
      ...this.state.project,
      accessCodeMapSetDescription: "",
      accuracyAndAdequacy: this.state.useAccuracy && this.state.useAdequacy,
      accuracySpecialtyDefinitionDesc: "",
      accuracySpecialtyToQdpCodeMapSetDescription: "",
      accuracyTemplateCollectionDescription: "",
      adequacyTemplateCollectionDescription: "",
      calculateStandards: false,
      copyProjectionProviders,
      copyProviderTerminations: !this.state.disableTerminationCopy,
      countyStatuses: await this.getCountyStatus(),
      createProjectOverrideMemberSets: (this.state.project.editProjectOverrideMemberSets ?? []),
      createSpecialtyGroupExceptions,
      description: this.state.project.resource.description,
      displayOnNetworkAccordion: this.state.project.displayOnNetworkAccordion,
      excludeDeactivatedFacilitiesImpact: this.state.excludeDeactivatedFacilityImpact,
      excludeDeactivatedFacilitiesNetwork: this.state.excludeDeactivatedFacilityNetwork,
      excludeDeactivatedProvidersImpact: this.state.excludeDeactivatedProviderImpact,
      excludeDeactivatedProvidersNetwork: this.state.excludeDeactivatedProviderNetwork,
      excludeInvalidNppesNpiFacilitiesImpact: this.state.excludeInvalidNppesNpiFacilityImpact,
      excludeInvalidNppesNpiFacilitiesNetwork: this.state.excludeInvalidNppesNpiFacilityNetwork,
      excludeInvalidNppesNpiProvidersImpact: this.state.excludeInvalidNppesNpiProviderImpact,
      excludeInvalidNppesNpiProvidersNetwork: this.state.excludeInvalidNppesNpiProviderNetwork,
      excludeMedicareOptOutProvidersImpact: this.state.excludeMedicareOptOutImpact,
      excludeMedicareOptOutProvidersNetwork: this.state.excludeMedicareOptOutNetwork,
      excludeMissingNpiFacilitiesImpact: this.state.excludeMissingNpiFacilityImpact,
      excludeMissingNpiFacilitiesNetwork: this.state.excludeMissingNpiFacilityNetwork,
      excludeMissingNpiProvidersImpact: this.state.excludeMissingNpiProviderImpact,
      excludeMissingNpiProvidersNetwork: this.state.excludeMissingNpiProviderNetwork,
      excludeOIGExcludedProvidersImpact: this.state.excludeOIGExclusionImpact,
      excludeOIGExcludedProvidersNetwork: this.state.excludeOIGExclusionNetwork,
      excludePrecludedProvidersImpact: this.state.excludePrecludedImpact,
      excludePrecludedProvidersNetwork: this.state.excludePrecludedNetwork,
      hasZipSummaries: this.state.project.hasZipSummaries,
      isPrivate: this.state.project.resource.isPrivate,
      marketProviderDescription: "",
      memberPopDescription: "",
      memberPopDescription2: "",
      name: this.state.project.resource.name,
      networkProviderDescription: "",
      networkTaxonomy: this.state.project.networkTaxonomy,
      prospectProviderDescription: "",
      retainNetNewProviders: this.state.retainNetNewProviders,
      shareUserIds,
      showAffiliationsInsights: this.state.project.showAffiliationsInsights,
      sourceCustomProjectId,
      timeDistanceCalculatorTypes: this.state.timeDistanceCalculatorTypes,
      useMarketProvidersForImpact: !!this.state.project.marketProviderSetId ? this.state.project.useMarketProvidersForImpact : false,
      useMarketProvidersForMarketAnalysis: !!this.state.project.marketProviderSetId ? this.state.project.useMarketProvidersForMarketAnalysis : false,
      useNetworkProvidersForMarketAnalysis: this.state.project.useNetworkProvidersForMarketAnalysis,
      useProspectProvidersForMarketAnalysis: !!this.state.project.prospectProviderSetId ? this.state.project.useProspectProvidersForMarketAnalysis : false
    } as CloneProjectModel;

    return cloneProjectModel;
  }

  private getCountyStatus = async () => {
    const statuses = this.state.isZipProject ? [] : await this.projectService.getCountyStatusInfo(
      this.state.project.projectId,
      this.state.project.serviceAreaRegionSetId,
      await this.projectService.maxRunConfigurationId(this.state.project, ProjectRunConfigurationType.DualMembershipPrimary),
    );

    return statuses;
  }

  private getAccuracySpecialtyGroups = async (specialtyDefinitionId: string) => {
    const specialtyDefinition = await this.accuracyTemplateService
      .getSpecialtyDefinitionById(specialtyDefinitionId);

    const specialtyGroups: TemplateSpecialtyGroup[] = [];

    for (const category of specialtyDefinition.categories) {
      for (const specialtyGroup of category.specialtyGroups) {
        const templateSpecialtyGroup: TemplateSpecialtyGroup = {
          code: specialtyGroup.code,
          displayOrder: specialtyGroup.displayOrder,
          hasTelehealthAccessPercentageCredits: null,
          name: specialtyGroup.name,
          servicingCountBy: null,
          servicingMinimumType: null,
        };

        specialtyGroups.push(templateSpecialtyGroup);
      }
    }
    return specialtyGroups && sortBy(specialtyGroups, this.sortOrder);
  }

  private exclusionsToSingleArray = async () => {
    const updatedNetworkExclusions: ProjectExclusionType[] = [];

    if (this.state.excludeDeactivatedFacilityNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.DeactivatedFacility);
    }

    if (this.state.excludeDeactivatedProviderNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.DeactivatedProvider);
    }

    if (this.state.excludeInvalidNppesNpiFacilityNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.InvalidNppesNpiFacility);
    }

    if (this.state.excludeInvalidNppesNpiProviderNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.InvalidNppesNpiProvider);
    }

    if (this.state.excludeMissingNpiFacilityNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.MissingNpiFacility);
    }

    if (this.state.excludeMissingNpiProviderNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.MissingNpiProvider);
    }

    if (this.state.excludeMedicareOptOutNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.MedicareOptOut);
    }

    if (this.state.excludeOIGExclusionNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.OIGExclusion);
    }

    if (this.state.excludePrecludedNetwork) {
      updatedNetworkExclusions.push(ProjectExclusionType.Precluded);
    }

    if (updatedNetworkExclusions.length === 0) {
      updatedNetworkExclusions.push(ProjectExclusionType.None);
    }

    const updatedImpactExclusions: ProjectExclusionType[] = [];

    if (this.state.excludeDeactivatedFacilityImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.DeactivatedFacility);
    }

    if (this.state.excludeDeactivatedProviderImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.DeactivatedProvider);
    }

    if (this.state.excludeInvalidNppesNpiFacilityImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.InvalidNppesNpiFacility);
    }

    if (this.state.excludeInvalidNppesNpiProviderImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.InvalidNppesNpiProvider);
    }

    if (this.state.excludeMissingNpiFacilityImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.MissingNpiFacility);
    }

    if (this.state.excludeMissingNpiProviderImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.MissingNpiProvider);
    }

    if (this.state.excludeMedicareOptOutImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.MedicareOptOut);
    }

    if (this.state.excludeOIGExclusionImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.OIGExclusion);
    }

    if (this.state.excludePrecludedImpact) {
      updatedImpactExclusions.push(ProjectExclusionType.Precluded);
    }

    if (updatedImpactExclusions.length === 0) {
      updatedImpactExclusions.push(ProjectExclusionType.None);
    }

    const project = {
      ...this.state.project,
      impactExclusions: updatedImpactExclusions,
      networkExclusions: updatedNetworkExclusions,
    };

    this.setState({ project });
  }
}

const WrappedCloneProjectOptions = withRouter(withUserContext(withFeatureFlagContext(CloneProject)));

export { WrappedCloneProjectOptions as CloneProject };
